// Config inventory (ARCH §13). The SINGLE source of truth for every gameplay
// number — "no magic numbers in logic files" (ARCH §1). When you add a constant
// here, add it to ARCH §13 in the same commit.
//
// This file is pure data: no imports, no DOM, no randomness. Values are the
// TerraForge contract; "parity" means within the documented tolerance of
// Minecraft Java 1.21.x (ARCH §7 fixes the formulas that consume these).

// ── Ticking ────────────────────────────────────────────────────────────────
export const TICK = {
  TPS: 20,
  MS_PER_TICK: 50,
  /** One full day/night cycle. */
  DAY_TICKS: 24_000,
  /** 1 redstone tick = 2 game ticks (RED-1). */
  REDSTONE_TICK_GT: 2,
  /** Random ticks applied per section per tick (GEN/PLR crop growth etc.). */
  RANDOM_TICK_SPEED: 3,
  /** Tick-accumulation cap; beyond this, drop time — no spiral of death (E-1). */
  MAX_CATCHUP_TICKS: 10,
} as const;

// ── Neighbour-notification bus (ARCH §27.9 / SCR-14, M46) ────────────────────
//    Every block edit enqueues notifications for its 6 face neighbours, drained
//    once per tick in the `scheduledTicks` phase. The cap is an ENGINE backstop,
//    distinct from SCR.CASCADE.MAX_DISPATCH (the script-visible dispatch clip):
//    the queue must stay O(cap) even with no script installed, because a flowing
//    ocean edits thousands of cells a tick through `setFluidBlock`.
export const NOTIFY = {
  /** Notifications that may be pending at once; past this they are DROPPED (not
   *  deferred) and counted, so a clip is visible to its author rather than silent
   *  (E-46). 16 384 = 2 730 edits' worth, which covers ordinary play and fluid
   *  flow with room to spare; a `SCR.FILL_MAX` fill's 24 576-notification burst
   *  deliberately exceeds it, because a 4096-cell fill is exactly the case §27.9
   *  says must be clipped rather than allowed to mint a dispatch per cell. */
  MAX_QUEUE: 16_384,
} as const;

// ── World shape (ENG-4..7) ───────────────────────────────────────────────────
export const WORLD = {
  MIN_Y: -64,
  HEIGHT: 384,
  /** Inclusive top build coordinate = MIN_Y + HEIGHT - 1 = 319. */
  MAX_Y: 319,
  SECTION: 16,
  /** 384 / 16. */
  SECTIONS_PER_COLUMN: 24,
  /** Section-array offset so section index = (y >> 4) + SECTION_Y_OFFSET (ENG-7). */
  SECTION_Y_OFFSET: 4,
  SEA_LEVEL: 63,
} as const;

// ── Overworld climate noise (GEN-3). Low frequencies → large biomes. Height
//    comes from continuous fields (continentalness/erosion/ridges) so it never
//    seams at biome borders; biome is a label on the same fields (GEN-4). ──────
export const CLIMATE = {
  TEMPERATURE_FREQ: 0.0018,
  HUMIDITY_FREQ: 0.0021,
  CONTINENTAL_FREQ: 0.0016,
  EROSION_FREQ: 0.0026,
  RIDGE_FREQ: 0.0034,
  /** Rolling surface detail (seam-free) and mountain jaggedness. */
  DETAIL_FREQ: 0.02,
  JAGGED_FREQ: 0.11,
} as const;

// ── Streaming & view (ENG-21) ────────────────────────────────────────────────
export const VIEW = {
  RENDER_DIST_DEFAULT: 8,
  // Slider ceiling (chunks). High values are heavy — the camera far-plane + fog
  // scale to match (boot render loop), and chunks stream in under a per-frame budget.
  RENDER_DIST_MAX: 128,
  /** Entity AI + random ticks only within this radius (ENG-21). */
  SIM_DIST: 6,
  /** Chunks beyond RENDER_DIST + this are evicted (ENG-21). */
  EVICT_MARGIN: 2,
} as const;

// ── Streaming pipeline tuning (perf) ─────────────────────────────────────────
//    An aircraft crosses ~7 chunks/second (AVI-2), far faster than the streamer
//    was tuned for. These knobs parallelize gen/mesh across cores and (M-perf)
//    bias prefetch ahead of a fast flight so the leading edge fills first.
export const STREAM = {
  /** Max mesh worker threads; the pool takes hardwareConcurrency−1 clamped here. */
  MESH_WORKERS_MAX: 6,
  /** Max gen worker threads (generation front-runs meshing, so it needs fewer). */
  GEN_WORKERS_MAX: 4,
  /** Baseline mesh jobs dispatched per frame across the whole pool. */
  MESH_REQUEST_BASE: 4,
  /** Baseline gen jobs requested per frame. */
  GEN_REQUEST_BASE: 8,
  /** Baseline geometry uploads per frame (main thread, ENG-15). */
  UPLOAD_BASE: 6,
  /** Upload ceiling per frame — lower than dispatch, since each upload is main-
   *  thread GPU-buffer work and too many at once would hitch a frame. */
  UPLOAD_MAX: 16,
  /** Extra jobs/frame per block/tick of player speed (forward-prefetch throughput). */
  SPEED_REQUEST_GAIN: 3,
  /** Hard ceiling on jobs/frame regardless of speed (never stall a frame streaming). */
  MAX_REQUEST_PER_FRAME: 24,
  /** Prefetch focus leads the player this many ticks along the velocity vector, so
   *  the leading edge of a fast flight fills before the wake. */
  PREFETCH_LEAD_TICKS: 40,
  /** The lead point is clamped to this fraction of the render radius. */
  PREFETCH_LEAD_FRAC: 0.6,
  /** Below this speed (blocks/tick) streaming stays symmetric (no forward bias). */
  MOVING_SPEED: 0.15,
  /** EMA factor smoothing the per-tick heading used for prefetch (0..1). */
  HEADING_SMOOTH: 0.25,
  /** A per-tick displacement above this (blocks/tick ≈ 1 chunk) is a teleport, not
   *  flight: ignored so prefetch never aims at a jump destination. */
  TELEPORT_SPEED: 16,
  // ── Surface-only LOD (perf): mesh just the top skin when flying high & fast, so
  //    a large render distance stays affordable. Only engages in an aircraft. ──
  /** Speed (blocks/tick) at/above which the fast-flight LOD may engage. */
  LOD_SPEED: 2.0,
  /** Height above local terrain (blocks) at/above which the LOD may engage. */
  LOD_ALTITUDE: 40,
  /** Hysteresis: drop back to full detail only below these (thresholds − band), so
   *  the LOD doesn't flip-flop when hovering around the engage point. */
  LOD_SPEED_HYST: 0.5,
  LOD_ALTITUDE_HYST: 10,
  /** Surface-only mesh depth: mesh a shell from each column's surface down this
   *  many blocks; deeper cells are skipped (invisible from altitude). */
  LOD_FLOOR_DEPTH: 24,
  // ── Adaptive render-distance cap (perf): pull the view in when the framerate
  //    can't sustain the streamed radius (esp. fast flight on a weak client). ──
  /** Below this fps the render-distance cap shrinks a step. Set under a typical
   *  weak-client ground framerate so it engages under load, not at rest. */
  FPS_LOW: 15,
  /** At/above this fps (with headroom under the user's setting) the cap grows back
   *  a step. The FPS_LOW…FPS_HIGH gap is the hysteresis band (no oscillation). */
  FPS_HIGH: 30,
  /** Floor the adaptive cap never drops below (matches the settings-slider min). */
  RENDER_MIN: 4,
  /** How often (ms) the adaptive cap steps, so the radius eases rather than snaps. */
  CAP_ADJUST_MS: 1000,
} as const;

// ── Interaction (ENG-19) ─────────────────────────────────────────────────────
export const REACH = {
  BLOCK: 4.5,
  ATTACK: 3.0,
} as const;

// ── Player physics (ENG-16..18; §5.3). Velocities in m/s, accel per-tick. ────
export const PLAYER = {
  AABB_WIDTH: 0.6,
  AABB_HEIGHT: 1.8,
  AABB_HEIGHT_SNEAK: 1.5,
  EYE_HEIGHT: 1.62,
  EYE_HEIGHT_SNEAK: 1.27,
  /** Per-tick gravitational acceleration for living entities. */
  GRAVITY: -0.08,
  /** Per-tick vertical velocity multiplier (air drag). */
  DRAG: 0.98,
  /** Instantaneous upward velocity on jump. Tuned above Minecraft's 0.42
   *  (apex 1.25) to 0.45 (apex ~1.42) so a 1-block obstacle clears comfortably. */
  JUMP_V: 0.45,
  WALK: 4.317,
  SPRINT: 5.612,
  SNEAK: 1.295,
  /** In-air steering: fraction of target speed added toward input per tick,
   *  capped at the walk/sprint speed. Lets you jump forward onto a ledge
   *  (full MC accel/friction movement is a later feel-gate refinement). */
  AIR_CONTROL: 0.3,
  /** Creative/spectator flight speed in m/s (sprint doubles it, D8). */
  FLY: 10.8,
  /** Ms between the two space taps that toggle flight (double-tap). */
  FLY_DOUBLE_TAP_MS: 300,
  /** Slow Falling clamps the descent to this per-tick velocity (PLR-8). */
  SLOW_FALL_CAP: -0.08,
} as const;

// ── Fluids (ENG-14; §5.5 step 4), in game ticks ──────────────────────────────
export const FLUIDS = {
  WATER_FLOW_DELAY: 5,
  LAVA_FLOW_DELAY: 30,
  LAVA_FLOW_DELAY_NETHER: 10,
} as const;

// ── Weather (PLR-10; §5.5 phase 1), in game ticks ────────────────────────────
export const WEATHER = {
  /** Clear-sky phase between rains (Minecraft ~0.5–10 in-game days). */
  CLEAR_MIN: 12_000,
  CLEAR_MAX: 180_000,
  /** Rain phase (0.5–1 day). */
  RAIN_MIN: 12_000,
  RAIN_MAX: 24_000,
  /** Thunder overlaps rain (~3–13 min). */
  THUNDER_MIN: 3_600,
  THUNDER_MAX: 15_600,
  THUNDER_CLEAR_MIN: 12_000,
  THUNDER_CLEAR_MAX: 180_000,
  /** Per-tick lightning-strike chance during a thunderstorm ≈ 1/100000 per player. */
  LIGHTNING_DENOM: 100_000,
  /** Lightning fire + damage (RED-8-adjacent). */
  LIGHTNING_DAMAGE: 5,
  LIGHTNING_FIRE_TICKS: 160,
} as const;

// ── Sleep + phantoms (PLR-10) ────────────────────────────────────────────────
export const SLEEP = {
  /** Sleeping is allowed after dusk / during a thunderstorm. */
  NIGHT_START: 12_542,
  NIGHT_END: 23_460,
  /** No hostile mob may be within this radius of the bed (blocks). */
  MONSTER_RADIUS: 8,
  /** Wake time-of-day the world advances to on a successful sleep. */
  WAKE_TIME: 0,
  /** Ticks awake before phantoms may spawn (3 in-game days). */
  PHANTOM_DAYS_TICKS: 72_000,
} as const;

// ── Swim physics (§5.3). Per-tick multipliers/accels while submerged. ────────
export const WATER = {
  H_DRAG: 0.85,
  V_DRAG: 0.8,
  GRAVITY: 0.02,
  SWIM_UP: 0.045,
} as const;

// ── Falling blocks (ENG; sand/gravel/concrete powder gravity, M10.1) ─────────
export const FALLING = {
  /** Per-tick downward acceleration (blocks/tick²). */
  GRAVITY: 0.04,
  /** Terminal fall speed (blocks/tick). */
  MAX_SPEED: 3.92,
} as const;

// ── Redstone (RED-2/3), in game ticks / blocks ───────────────────────────────
export const REDSTONE = {
  HOPPER_COOLDOWN: 8,
  PISTON_PUSH_LIMIT: 12,
} as const;

// ── Redstone signal engine (RED-1/2/4; M15.1). See docs/redstone-semantics.md.
// Delays are in redstone ticks (rt); 1 rt = TICK_GT game ticks. ──────────────
export const RED = {
  /** Game ticks per redstone tick (Java: 2 gt). */
  TICK_GT: 2,
  /** Signal ceiling. */
  MAX_POWER: 15,
  /** Safety cap on cells solved per dirty flood (runaway guard). */
  MAX_NETWORK: 8192,
  /** Redstone ticks a stone button stays pressed after a use. */
  BUTTON_TICKS: 10,
  /** Torch inversion delay. */
  TORCH_DELAY: 1,
  /** Comparator recompute delay. */
  COMPARATOR_DELAY: 1,
  /** State changes within BURNOUT_WINDOW rt that trip a torch's burnout. */
  BURNOUT_CHANGES: 8,
  /** Sliding window (rt) burnout changes are counted over. */
  BURNOUT_WINDOW: 30,
  /** Forced-off cooldown (rt) after a torch burns out. */
  BURNOUT_OFF: 60,
} as const;

// ── Primed TNT (RED-8; M15.2.2). Physics mirrors a falling block; the blast
// reuses explodeAt at POWER 4 (the RED-8 golden). ────────────────────────────
export const TNT = {
  /** Fuse length in game ticks (Java: 80 gt = 4 s). */
  FUSE_GT: 80,
  /** Blast power (Java TNT = 4). */
  POWER: 4,
  /** Per-tick downward acceleration (blocks/tick²). */
  GRAVITY: 0.04,
  /** Air drag applied to velocity each tick. */
  DRAG: 0.98,
  /** Upward pop velocity on ignition (blocks/tick) — TNT hops when it primes. */
  POP_UP: 0.2,
  /** Horizontal ignition scatter (± blocks/tick), applied with an rng. */
  POP_SPREAD: 0.02,
  /** A TNT caught in another blast re-primes with a short fuse in
   *  [CHAIN_FUSE_MIN, CHAIN_FUSE_MIN + CHAIN_FUSE_SPREAD) gt (the chain reaction). */
  CHAIN_FUSE_MIN: 10,
  CHAIN_FUSE_SPREAD: 20,
} as const;

/** @deprecated use TNT.FUSE_GT. */
export const TNT_FUSE_GT = TNT.FUSE_GT;

// ── Minecarts on rails (RED-5/6; M16.2). The cart follows the rail centreline
// (rail-relative progress `t` per cell); speed is in blocks/tick. ────────────
export const MINECART = {
  /** Speed cap (Java empty cart: 8 m/s = 0.4 blocks/tick). Kept < 0.5 so a cart
   *  crosses at most one rail landmark (centre / cell edge) per tick. */
  MAX_SPEED: 0.4,
  /** Per-tick push a powered rail adds while it is powered. */
  POWERED_ACCEL: 0.06,
  /** Rolling friction multiplier on a normal rail each tick. */
  DRAG: 0.98,
  /** Speed multiplier per tick on an UNpowered powered-rail (a brake). */
  BRAKE: 0.5,
  /** Below this speed the cart snaps to rest. */
  STOP_EPS: 0.005,
  /** At/under this speed a powered rail re-chooses a launch direction. */
  LAUNCH_MIN: 0.02,
  /** Off-rail deceleration multiplier (a derailed cart coasts to a stop). */
  DERAIL_DRAG: 0.9,
  /** Cart render height above its rail cell's floor. */
  RAIL_Y: 0.15,
} as const;

// ── Boats (RED-6 / M16.2 water transport) ────────────────────────────────────
export const BOAT = {
  /** Forward paddle acceleration per tick. */
  ACCEL: 0.04,
  /** Yaw turn (radians) per tick while steering. */
  TURN: 0.06,
  /** Horizontal water drag each tick (a boat glides then coasts to a stop). */
  H_DRAG: 0.9,
  /** Below this horizontal speed the boat snaps to rest. */
  STOP_EPS: 0.002,
  /** Gravity applied per tick when the boat is out of water (it falls/sinks). */
  GRAVITY: 0.04,
  /** How deep the hull rides below the water surface (draft). */
  DRAFT: 0.12,
  /** Buoyancy spring strength pulling the hull toward its floating line. */
  BUOY: 0.3,
  /** Vertical damping so buoyancy settles instead of bobbing forever. */
  V_DRAG: 0.7,
  /** Half-width used for the boat's blunt solid collision. */
  HALF: 0.7,
} as const;

// ── Dimensions & portals (DIM-2) ─────────────────────────────────────────────
export const PORTAL = {
  /** Ticks standing in a portal before transfer (instant in creative). */
  WAIT_GT: 80,
  /** Destination portal search radius, in columns, per side of travel. */
  SEARCH_OVERWORLD: 128,
  SEARCH_NETHER: 16,
  /** Overworld:nether horizontal coordinate ratio (÷8 going down, ×8 coming up). */
  COORD_RATIO: 8,
  /** Minimum nether-portal interior (width × height) — 2×3 (DIM-2). */
  MIN_WIDTH: 2,
  MIN_HEIGHT: 3,
  /** Maximum nether-portal interior — 21×21 (DIM-2). */
  MAX_WIDTH: 21,
  MAX_HEIGHT: 21,
} as const;

// ── Nether generation (GEN-10; wastes for M7). Nether world height 0..127. ────
export const NETHER = {
  /** Absolute-Y bands of the nether, expressed inside its host range (TAL-5).
   *  These were global WORLD constants; they are dimension-generator config,
   *  not world shape. The nether still occupies 0..127 of the default range. */
  HEIGHT: 128,
  /** Nether lava ocean surface (GEN-10). */
  LAVA_SEA: 31,
  /** Bedrock roof top (inclusive); the playable ceiling is just below it. */
  ROOF_Y: 127,
  /** Bedrock bands: solid at 0 and 127, roughened over 1..4 and 123..126. */
  BEDROCK_FLOOR_TOP: 4,
  BEDROCK_ROOF_BOTTOM: 123,
  /** Netherrack floor top: mean Y + fbm amplitude (2D field). */
  FLOOR_BASE: 40,
  FLOOR_AMP: 22,
  FLOOR_FREQ: 0.026,
  /** Netherrack ceiling bottom: mean Y − fbm amplitude. */
  CEIL_BASE: 104,
  CEIL_AMP: 14,
  CEIL_FREQ: 0.03,
  /** 3D cavern carve of the netherrack body. */
  CAVE_FREQ: 0.05,
  CAVE_THRESH: 0.34,
  /** Glowstone ceiling blobs per chunk (rng). */
  GLOWSTONE_TRIES: 6,
  /** Nether-quartz ore veins per chunk + vein size (M7.2). */
  QUARTZ_TRIES: 16,
  QUARTZ_VEIN: 6,
  /** Nether-gold ore veins per chunk + vein size — the same band as quartz but
   *  rarer, and it drops nuggets rather than the block. */
  NETHER_GOLD_TRIES: 8,
  NETHER_GOLD_VEIN: 5,
  /** Ancient debris (netherite's source). Two bands, exactly as Minecraft: a
   *  dense one around Y 15 and a thin scatter through the rest of the rock. Veins
   *  are TINY — a strip mine is meant to be a real expedition. */
  DEBRIS_TRIES: 1,
  DEBRIS_VEIN: 3,
  DEBRIS_MAIN_MIN: 8,
  DEBRIS_MAIN_MAX: 22,
  DEBRIS_UPPER_MIN: 23,
  DEBRIS_UPPER_MAX: 119,
  /** 1 in N debris attempts uses the thin upper band instead of the main one. */
  DEBRIS_UPPER_ONE_IN: 4,
} as const;

// ── Nether biomes (GEN-10 extension) ─────────────────────────────────────────
// Two salted 2D fields pick one of five biomes per column; the biome repaints the
// exposed floor only (terrain shape is untouched, so the M7 caverns stay exact).
export const NETHER_BIOME = {
  /** Field frequency — biome patches roughly a few chunks across. */
  FREQ: 0.006,
  THRESHOLD: {
    /** |weird| above this splits off the two "corner" biomes. */
    WEIRD: 0.34,
    /** heat above/below these give crimson / warped forest. */
    HOT: 0.22,
    COLD: -0.22,
  },
  /** How far below the ceiling the surface scan starts looking for a floor. */
  SURFACE_SCAN_MARGIN: 1,
  /** Basalt-delta pillar attempts per chunk, and their max height. */
  DELTA_PILLAR_TRIES: 5,
  DELTA_PILLAR_MAX: 5,
  /** Fungal-forest flora attempts per chunk (most become ground cover). */
  FUNGUS_TRIES: 24,
  /** 1-in-N of those grow into a huge fungus instead of ground cover. */
  HUGE_FUNGUS_ONE_IN: 6,
  /** Huge-fungus stem height range. */
  FUNGUS_MIN_H: 4,
  FUNGUS_MAX_H: 9,
  /** Vine strands hung off a huge-fungus canopy, and their generated length. */
  CANOPY_VINE_TRIES: 6,
  VINE_MIN_LEN: 2,
  VINE_MAX_LEN: 8,
  /** Weeping-vine strands hung from the crimson forest's rocky ceiling per chunk. */
  CEILING_VINE_TRIES: 6,
} as const;

// ── Nether vines (GEN-10 extension). A run of segments that extends from its tip
//    in the species' own direction — down for weeping, up for twisting. ─────────
export const VINE = {
  /** Longest run a vine grows to, counted from its anchor. */
  MAX_LENGTH: 26,
  /** Per random-tick chance the tip extends one segment. */
  GROW_CHANCE: 0.1,
  /** Bone meal adds a burst this many segments long, not a single one. */
  BONE_MEAL_MIN: 1,
  BONE_MEAL_MAX: 5,
} as const;

// ── Aquifers + lush caves (GEN-6 extension) ──────────────────────────────────
// Both ride on the existing carve: they change what a carved cell BECOMES, never
// which cells are carved, so a world generated without them is byte-identical.
export const AQUIFER = {
  /** Water-table field: mean Y plus a wobble. */
  BASE_Y: 8,
  AMP: 22,
  FREQ: 0.008,
  /** Hard ceiling — aquifers never reach up into surface caves. */
  MAX_Y: 44,
  /** A column must stand this far ABOVE sea level to hold an aquifer. Keeps the
   *  pass off the coast entirely, so the E-6 "a cave never opens into the ocean"
   *  invariant is untouched: aquifer water and sea water are never the same body. */
  LAND_MARGIN: 6,
} as const;

export const LUSH = {
  /** Lushness field frequency and the threshold a column must clear. */
  FREQ: 0.011,
  THRESHOLD: 0.30,
  /** 1-in-N chance a lush ceiling cell hangs a vine, and that a vine bears berries. */
  VINE_ONE_IN: 3,
  BERRY_ONE_IN: 3,
  /** 1-in-N chance a lush ceiling cell instead crusts over with glow lichen. */
  LICHEN_ONE_IN: 4,
  /** Max vine length hanging from a ceiling. */
  VINE_MAX: 4,
  /** 1-in-N chance a mossy floor cell also grows a moss carpet on top. */
  CARPET_ONE_IN: 3,
  /** Clay pools: 1-in-N mossy floor cells seed one, and how wide it spreads. A pool
   *  is a shallow clay basin holding water — the thing a big dripleaf grows out of.
   *  The rate looks generous because the seed cell must ALSO clear the lushness
   *  field and the inland/dry rule a free water surface needs; at 1-in-30 that comes
   *  out around a dozen pools per 256-block square. */
  CLAY_POOL_ONE_IN: 30,
  CLAY_POOL_RADIUS: 2,
  /** 1-in-N cells of a pool's clay rim grow a dripleaf, 1-in-M of them the big kind. */
  DRIPLEAF_ONE_IN: 3,
  BIG_DRIPLEAF_ONE_IN: 3,
  /** 1-in-N lush ceiling cells turn to rooted dirt and dangle roots below. */
  ROOTED_ONE_IN: 14,
  ROOT_MAX: 3,
  /** 1-in-N lush ceiling cells instead bloom a spore blossom. */
  SPORE_ONE_IN: 90,
  /** 1-in-N mossy floor cells grow an azalea bush; 1-in-M of those are flowering. */
  AZALEA_ONE_IN: 60,
  FLOWERING_ONE_IN: 3,
} as const;

// ── The End (DIM-3) ──────────────────────────────────────────────────────────
// A void dimension holding one central end-stone island ringed by obsidian
// pillars, and sparse outer islands beyond a wide gap. No bedrock shell, no sea.
export const END = {
  /** Top Y of the central island's surface. */
  ISLAND_TOP: 64,
  /** Central island radius and its maximum (centre) thickness. */
  ISLAND_RADIUS: 56,
  ISLAND_THICKNESS: 14,
  /** Rim wobble so the disc isn't a perfect circle. */
  SHAPE_FREQ: 0.02,
  SHAPE_AMP: 9,
  /** Obsidian pillar ring around the island (the dragon's crystal towers). */
  PILLAR_COUNT: 10,
  PILLAR_RING_RADIUS: 42,
  PILLAR_RING_JITTER: 4,
  PILLAR_MIN_H: 12,
  PILLAR_MAX_H: 28,
  PILLAR_MIN_R: 2,
  PILLAR_MAX_R: 4,
  /** Outer islands: nothing until this radius, then shards that thin with distance. */
  OUTER_START: 640,
  OUTER_FADE: 3200,
  OUTER_FREQ: 0.012,
  OUTER_THRESH: 0.22,
  OUTER_THRESH_FADE: 0.16,
  OUTER_MIN_Y: 40,
  OUTER_MAX_Y: 88,
  OUTER_CENTRE_Y: 64,
  /** >1 squashes shards vertically so they read as islands, not blobs. */
  OUTER_Y_SQUASH: 2.6,
  /** The obsidian arrival platform (MC's fixed x=100 landing pad). */
  PLATFORM_X: 100,
  PLATFORM_Y: 48,
  PLATFORM_Z: 0,
  PLATFORM_RADIUS: 2,
  PLATFORM_HEADROOM: 3,
  /** How high over the arena the ender dragon enters. */
  DRAGON_SPAWN_HEIGHT: 24,
  /** Exit-portal footprint radius and the height of its egg plinth. */
  EXIT_PORTAL_RADIUS: 2,
  EXIT_PLINTH_HEIGHT: 3,
  /** Chorus-plant attempts per outer-island chunk, and stalk height range. */
  CHORUS_TRIES: 5,
  CHORUS_MIN_H: 2,
  CHORUS_MAX_H: 5,
  /** End gateways (DIM-3): the ring of bedrock nests a slain dragon leaves floating
   *  over the main island, each a one-way jump out to the outer islands. */
  GATEWAY_COUNT: 20,
  GATEWAY_RADIUS: 96,
  GATEWAY_Y: 75,
  /** Where an outbound gateway lands you: the same bearing, just past the void gap
   *  (OUTER_START), so you arrive among the shards rather than short of them. */
  GATEWAY_EXIT_RADIUS: 704,
  /** The end-stone pad a gateway lays when it lands you over open void, and how far
   *  above the landing its return gateway floats. */
  GATEWAY_PAD_RADIUS: 2,
  GATEWAY_PAD_Y: 64,
  GATEWAY_PAD_HEADROOM: 3,
  GATEWAY_RETURN_HEIGHT: 10,
  /** How far around its nominal column a gateway may hunt for REAL ground before
   *  giving up and laying a pad. Adjacent gateways' nominal columns are
   *  2·EXIT_RADIUS·sin(π/COUNT) ≈ 220 blocks apart, so these bounds keep every
   *  gateway's search box disjoint from its neighbours' — two gateways can never
   *  converge on the same shard however the terrain falls. */
  GATEWAY_ISLAND_RADIAL: 96,
  GATEWAY_ISLAND_LATERAL: 32,
  GATEWAY_ISLAND_STEP: 16,
} as const;

// ── Llama caravans (M61). A leashed llama tows the ones behind it. ───────────
export const CARAVAN = {
  /** Llamas in one train, the leader included (Minecraft's cap). */
  MAX_LENGTH: 10,
  /** How far behind its leader a follower settles, in blocks. */
  FOLLOW_DISTANCE: 2.5,
  /** Past this the lead snaps and the follower is unhitched, rather than being
   *  dragged through the terrain between them. */
  SNAP_DISTANCE: 12,
} as const;

// ── Chorus fruit (DIM-3). Eating one blinks you a short way, as in Minecraft. ──
export const CHORUS = {
  /** Half-width of the offset box each attempt samples, in blocks. */
  TELEPORT_RANGE: 8,
  /** Attempts before the fruit is simply eaten with no jump. */
  TRIES: 16,
  /** How far below a sampled height the search falls looking for a floor. */
  DROP: 8,
} as const;

// ── Fire / burn status (PLR; lava + ghast fireball, M7.2) ────────────────────
export const BURN = {
  /** Fire ticks applied by lava contact (4 s), decays when out of lava. */
  LAVA_FIRE_TICKS: 80,
  /** Direct lava damage cadence + amount while standing in lava. */
  LAVA_DAMAGE_INTERVAL: 10,
  LAVA_DAMAGE: 4,
  /** Being-on-fire damage cadence + amount. */
  FIRE_DAMAGE_INTERVAL: 10,
  FIRE_DAMAGE: 1,
} as const;

// ── Ghast fireball (MOB-5 flying; RED-8 explosion power 1) ────────────────────
export const FIREBALL = {
  /** Straight-line launch speed (blocks/tick); no gravity. */
  SPEED: 0.35,
  /** Explosion power on impact (RED-8: ghast fireball = 1). */
  POWER: 1,
  /** Fire ticks applied to a struck player. */
  FIRE_TICKS: 100,
  LIFETIME: 200,
} as const;

// ── Flying-mob AI (MOB-2 flying: ghast). 3D hover/wander/approach. ────────────
export const FLYING = {
  /** Preferred hover height above the netherrack floor. */
  HOVER_HEIGHT: 22,
  /** Per-tick steering acceleration toward the wander/approach target. */
  ACCEL: 0.02,
  /** Max flight speed (blocks/tick). */
  MAX_SPEED: 0.14,
  /** Picks a new wander target roughly this often (ticks). */
  WANDER_INTERVAL: 80,
  /** Fires at a player within this range with line of sight. */
  ATTACK_RANGE: 48,
} as const;

// ── Mobs (MOB-1..5) ──────────────────────────────────────────────────────────
export const MOB = {
  /** Per-category active mob caps. */
  CAPS: {
    monster: 70,
    creature: 12,
    ambient: 8,
    water_creature: 6,
    water_ambient: 12,
  },
  DESPAWN: {
    /** Random despawn eligible beyond this distance. */
    SOFT: 32,
    /** Instant despawn beyond this distance. */
    HARD: 128,
    /** Idle ticks (no player within SOFT) before random despawn is eligible (30 s). */
    IDLE_TICKS: 600,
    /** Per-tick random despawn chance denominator once eligible (1/800). */
    RANDOM_DENOM: 800,
  },
  /** Living-entity physics: shares the player's gravity/drag (§5.3). Mobs jump a
   *  1-high obstacle rather than step-up (MOB-3). */
  GRAVITY: 0.08,
  DRAG: 0.98,
  JUMP_V: 0.42,
  /** Speed multiplier turning a mob's blocks/s `speed` stat into per-tick velocity. */
  SPEED_TO_TICK: 1 / 20,
  SPAWN: {
    /** Hostiles spawn only where block light ≤ this (surface also needs night). */
    MONSTER_LIGHT_MAX: 0,
    /** Ambient fliers (bats) spawn only where block light ≤ this (dark), at night
     *  or underground. */
    AMBIENT_LIGHT_MAX: 3,
    /** Passive creatures need light ≥ this (grass, daytime). */
    CREATURE_LIGHT_MIN: 9,
    /** Spawn attempts per category per spawn phase. */
    ATTEMPTS: 3,
    /** No spawn closer to a player than this (blocks). */
    MIN_RADIUS: 24,
    /** …nor farther than this. */
    MAX_RADIUS: 44,
    /** Pack members scatter within this horizontal radius of the pack origin. */
    PACK_SPREAD: 4,
    /** Spawn cycle runs every N ticks (not every tick — MC runs on a cadence). */
    INTERVAL: 20,
  },
  /** Melee AI (MOB-2 melee, PLR-3). */
  MELEE: {
    /** Ticks between a mob's attacks. */
    COOLDOWN: 20,
    /** Horizontal knockback speed applied to a struck target. */
    KNOCKBACK: 0.42,
    /** Extra reach beyond the two AABB half-widths for an attack to land. */
    REACH_PAD: 0.4,
  },
  /** Wander AI: a mob picks a new heading roughly this often (ticks). */
  WANDER_INTERVAL: 60,
  /** Baby scale (MOB-2) — half size, and half-length breeding cooldown timer. */
  BABY_SCALE: 0.5,
  /** Greedy-steer pathing (MOB-3). */
  STEER: {
    /** Drops deeper than this (blocks) are avoided; ≤ this the mob walks off. */
    MAX_DROP: 3,
    /** How far ahead (blocks) the obstacle/drop probe looks. */
    PROBE: 0.6,
  },
} as const;

// ── Projectiles (MOB-5). Arrow ballistics; per-tick gravity/drag. ─────────────
export const PROJECTILE = {
  ARROW_GRAVITY: 0.05,
  ARROW_DRAG: 0.99,
  /** Launch speed (blocks/tick) at full draw. */
  ARROW_SPEED: 1.6,
  /** Damage = round(speed · this), clamped ≥ 1 (skeleton ≈ base). */
  ARROW_DAMAGE_PER_SPEED: 2.5,
  /** Ticks before a fired arrow despawns if it hits nothing. */
  ARROW_LIFETIME: 200,
} as const;

// ── Firework rocket (MB.7). A self-propelled, damageless rocket a dispenser
//    launches in its facing direction; it accelerates along its heading, then
//    "detonates" (removes itself → the client's burst) after a short fuse. The
//    sim only tracks the flight + fuse; the pop is cosmetic. ─────────────────
export const FIREWORK = {
  /** Ticks aloft before it detonates (~1.1 s). */
  FUSE: 22,
  /** Self-propulsion accel along the heading per tick. */
  THRUST: 0.06,
  /** Velocity retention per tick. */
  DRAG: 0.99,
  /** Initial launch speed (blocks/tick) out of a dispenser. */
  LAUNCH_SPEED: 0.45,
} as const;

// ── Splash potion (MB.6). A thrown, non-sticking potion that shatters into an
//    area cloud on impact (witch → harmful splashes). Flies with the arrow
//    integrator; the AoE falloff math lives in game/potion.ts. ───────────────
export const POTION = {
  /** Launch speed (blocks/tick) of a thrown splash potion. */
  THROW_SPEED: 1.2,
  /** Ticks between a witch's splash throws. */
  THROW_INTERVAL: 60,
} as const;

// ── XP orbs (PLR-5). Merge/magnet/absorb like item entities but grant levels. ─
export const XP_ORB = {
  PICKUP_RADIUS: 1.1,
  MAGNET_RADIUS: 3.5,
  MAGNET_ACCEL: 0.06,
  MERGE_RADIUS: 0.6,
  DESPAWN_TICKS: 6000,
  GRAVITY: 0.04,
  DRAG: 0.98,
} as const;

// ── Breeding (MOB-2 grazer). Times in game ticks. ────────────────────────────
export const BREED = {
  /** Ticks a fed animal stays "in love" seeking a mate. */
  LOVE_TICKS: 600,
  /** Ticks before an animal can breed again (5 min). */
  COOLDOWN_TICKS: 6000,
  /** Ticks for a baby to grow up (20 min). */
  GROW_TICKS: 24_000,
  /** Search radius for a nearby in-love partner (blocks). */
  MATE_RADIUS: 8,
  /** XP granted to the breeder on a successful breed (1..7). */
  XP_MIN: 1,
  XP_MAX: 7,
} as const;

// ── Creeper (MOB special; RED-8 explosion power 3). ──────────────────────────
export const CREEPER = {
  /** Distance at which a creeper starts its fuse. */
  TRIGGER_RADIUS: 3,
  /** Distance at which a lit creeper aborts (target fled). */
  RELEASE_RADIUS: 7,
  /** Fuse length in game ticks (1.5 s). */
  FUSE_TICKS: 30,
  /** Explosion power (RED-8): creeper 3. */
  POWER: 3,
} as const;

// ── Tamable mobs (MOB-2 tamable: wolf/cat, M13.1). ───────────────────────────
export const TAME = {
  /** Player-follow distance band: closer than MIN it holds, past MAX it catches up. */
  FOLLOW_MIN: 2,
  FOLLOW_MAX: 10,
  /** Teleport to the owner when this far away (blocks) — a lost pet re-homes. */
  TELEPORT_DIST: 20,
  /** A tamed pet stays near its sit/owner anchor within this radius when idle. */
  WANDER_RADIUS: 6,
  /** Neutral-mob anger window after being struck (ticks) — a hit wolf retaliates. */
  ANGER_TICKS: 500,
  /** Owner-assist target validity window (ticks) after the owner hits a mob. */
  ASSIST_TICKS: 200,
} as const;

// ── Golems (MOB-2 golem: iron/snow, constructed — M13.1). ────────────────────
export const GOLEM = {
  /** Hostile acquisition range (blocks). */
  TARGET_RANGE: 16,
  /** Iron golem melee knockback launch (tosses a mob into the air). */
  TOSS_V: 0.5,
  /** Ticks between snow-golem snowball throws. */
  SNOWBALL_INTERVAL: 20,
  /** Snowball launch speed (blocks/tick) and its token damage (playability). */
  SNOWBALL_SPEED: 1.4,
  SNOWBALL_DAMAGE: 1,
  /** A snow golem takes this damage per tick while wet (rain / water). */
  MELT_DAMAGE: 1,
} as const;

// ── Sheep (grazer extension: shear / regrow — M13.1). ────────────────────────
export const SHEEP = {
  /** Per random-tick chance (denominator) a sheared sheep eats grass to regrow wool. */
  REGROW_DENOM: 40,
} as const;

// ── Enderman (MOB special, M13.2). Neutral until looked-at or struck. ─────────
export const ENDERMAN = {
  /** Look-trigger range (blocks) — a player staring within this angers it. */
  LOOK_RANGE: 32,
  /** Cosine of the stare cone half-angle: dot(playerLook, →enderman) above this
   *  = "looking at it" (≈0.985 ≈ 10°). */
  LOOK_DOT: 0.985,
  /** Blink-teleport search radius (blocks) + attempts to find a standable spot. */
  TELEPORT_RANGE: 24,
  TELEPORT_TRIES: 12,
  /** Ticks between teleports (so a hit enderman doesn't strobe). */
  TELEPORT_COOLDOWN: 20,
  /** Water/rain damage per cadence (endermen hate water). */
  WATER_DAMAGE: 1,
  /** Per-tick chance denominator to pick up / set down a carried block. */
  CARRY_DENOM: 400,
} as const;

// ── Slime (MOB special, M13.2). Cube that hops and splits on death. ──────────
export const SLIME = {
  /** Largest natural size; halves on each split (4 → 2 → 1). */
  MAX_SIZE: 4,
  /** Ticks between hops while chasing. */
  JUMP_INTERVAL: 16,
  /** A splitting slime spawns this many children (each half the size). */
  SPLIT_MIN: 2,
  SPLIT_MAX: 4,
  /** Per-size melee damage = size (0 for the smallest). */
} as const;

// ── Charged creeper (MOB special, M13.2). Lightning-struck → bigger blast. ───
export const CHARGED_CREEPER = {
  /** Explosion-power multiplier when a creeper is charged (RED-8). */
  POWER_MULT: 2,
  /** A lightning bolt charges creepers within this radius (blocks). */
  CHARGE_RADIUS: 3,
} as const;

// ── Raids (RAID; M14.2). Bad-omen capture → wave assault on a village. ────────
export const RAID = {
  /** A player carrying Bad Omen this close to a villager triggers a raid (blocks). */
  TRIGGER_RADIUS: 32,
  /** Bad Omen duration granted by killing a raid captain (game ticks). */
  BAD_OMEN_TICKS: 120_000,
  /** Hero of the Village duration granted on a raid victory (game ticks). */
  HERO_TICKS: 48_000,
  /** Raiders spawn on a ring this far from the raid centre (blocks). */
  SPAWN_RING: 20,
  /** A raider that strays this far from the raid centre is culled (blocks). */
  LEASH: 96,
  /** The raid ends in defeat if the player abandons the centre by this far (blocks). */
  ABANDON_RADIUS: 100,
} as const;

// ── Survival stats (PLR-1/4/9; §3.4). Health/hunger are 0..20 points (10 hearts,
//    10 drumsticks). Air is in ticks. Exhaustion/regen thresholds drive M4.3. ──
export const SURVIVAL = {
  MAX_HEALTH: 20,
  MAX_HUNGER: 20,
  /** Fresh spawn saturation (survival start / respawn). */
  START_HUNGER: 20,
  START_SATURATION: 5,
  /** Underwater air, in ticks (15 s); drowning damage after it hits 0. */
  MAX_AIR: 300,
  /** Exhaustion needed to drain 1 saturation (then 1 hunger) — PLR-4. */
  EXHAUSTION_PER_DRAIN: 4.0,
  /** Exhaustion added by: walking/m, sprinting/m, jump, sprint-jump, attack, damage. */
  EXHAUST_SPRINT_PER_M: 0.1,
  EXHAUST_JUMP: 0.05,
  EXHAUST_SPRINT_JUMP: 0.2,
  EXHAUST_ATTACK: 0.1,
  EXHAUST_DAMAGE: 0.1,
  /** Passive regen: +1 health / this many ticks while hunger ≥ REGEN_HUNGER. */
  REGEN_TICKS: 80,
  REGEN_HUNGER: 18,
  /** Starvation: −1 health / this many ticks at hunger 0. */
  STARVE_TICKS: 80,
  /** Drowning: −1 health / this many ticks once air is exhausted (E-8 adjacent). */
  DROWN_TICKS: 20,
  /** Fall damage: hearts = floor(fallDistance − this) (PLR-9); water zeroes (E-8). */
  FALL_SAFE_BLOCKS: 3,
} as const;

// ── Combat gear v2 (PLR-3; M17). Armour/enchant mitigation tuning, shield block
//    arc, mace smash-damage curve, totem of undying. The armour *material* stats
//    (points/toughness per piece) live with the item data; these are the shared
//    formula constants the pure `game/gear.ts` engine reads. ───────────────────
export const COMBAT = {
  /** Enchant Protection Factor: `damage ·= 1 − min(EPF, CAP)/25` (= ·0.04). */
  EPF_CAP: 20,
  EPF_DIVISOR: 25,
  /** Per-level EPF each protection enchant contributes to its damage type. */
  EPF: {
    /** Protection: applies to every damage type. */
    PROTECTION_ALL: 1,
    /** Fire/Blast/Projectile protection: type-specific bonus per level. */
    FIRE_PROTECTION: 2,
    BLAST_PROTECTION: 2,
    PROJECTILE_PROTECTION: 2,
    /** Feather Falling: fall-damage only. */
    FEATHER_FALLING: 3,
  },
  /** Shield: blocks a hit whose source lies within ±this many degrees of facing
   *  (frontal 180° arc). An axe hit disables the shield for DISABLE_TICKS. */
  SHIELD_ARC_DEG: 90,
  SHIELD_DISABLE_TICKS: 100,
  /** Mace smash bonus (M17): extra damage per block fallen, in three tiers —
   *  +HIGH for the first HIGH_UNTIL blocks, +MID for the next MID_SPAN, +LOW
   *  beyond. Only applies while falling (fallDistance > 0). */
  MACE: {
    HIGH: 4, HIGH_UNTIL: 3,
    MID: 2, MID_SPAN: 5,
    LOW: 1,
  },
  /** Totem of undying (M17): a lethal hit is cancelled, health set to REVIVE_HP,
   *  and these effects granted (id, amplifier, ticks). */
  TOTEM: {
    REVIVE_HP: 1,
    EFFECTS: [
      { id: 'regeneration', amplifier: 1, ticks: 900 },
      { id: 'absorption', amplifier: 1, ticks: 100 },
      { id: 'fire_resistance', amplifier: 0, ticks: 800 },
    ],
  },
} as const;

// ── Tools & break time (PLR-2). Base mining speed by tier; divisor 30 when the
//    tool can harvest the block (drops), 100 otherwise. ────────────────────────
export const TOOLS = {
  SPEED: { wood: 2, stone: 4, iron: 6, diamond: 8, netherite: 9, gold: 12 } as Readonly<Record<string, number>>,
  DIV_CORRECT: 30,
  DIV_WRONG: 100,
} as const;

// ── Item entities (E-17; PLR pickups). Radii in blocks, times in game ticks. ──
export const ITEMDROP = {
  /** Auto-pickup radius (Minecraft ≈ 1 block to the item centre). */
  PICKUP_RADIUS: 1.1,
  /** Beyond pickup, items drift toward a nearby player. */
  MAGNET_RADIUS: 2.2,
  MAGNET_ACCEL: 0.05,
  /** Same-item stacks within this merge (E-17). */
  MERGE_RADIUS: 0.6,
  /** Ticks before a fresh drop can be collected (throw-then-repickup guard). */
  PICKUP_DELAY: 10,
  /** 5-minute despawn (E-17). */
  DESPAWN_TICKS: 6000,
  /** Per-tick gravity + ground rest for a loose item. */
  GRAVITY: 0.04,
  DRAG: 0.98,
  /** Per-tick horizontal shove from a flowing fluid the item sits in (E-10). */
  FLUID_PUSH: 0.014,
} as const;

// ── Elytra glide flight (M21 — The End) ──────────────────────────────────────
// Per-tick aerodynamic constants mirroring Minecraft 1.21's fall-flying travel
// branch. The lift/dive/climb/steer coefficients live here so game/elytra.ts and
// the glide-ratio test single-source them.
export const ELYTRA = {
  /** Per-tick aerodynamic drag (horizontal / vertical) — MC's 0.99 / 0.98. */
  H_DRAG: 0.99,
  V_DRAG: 0.98,
  /** Lift fraction: gravity is offset by cos²(pitch)·this (level flight ≈ ¼ g). */
  LIFT: 0.75,
  /** Diving converts descent into forward speed at this rate. */
  DIVE_GAIN: 0.1,
  /** Climbing (looking up) converts forward speed into lift at these rates. */
  CLIMB_RATE: 0.04,
  CLIMB_LIFT: 3.2,
  /** Horizontal steer toward the look heading, per tick. */
  STEER: 0.1,
  /** How long one firework rocket boosts a glide (ticks); the boost is damageless. */
  FIREWORK_BOOST_TICKS: 20,
} as const;

// ── Experience (PLR-5, MOB-6) ────────────────────────────────────────────────
export const XP = {
  DRAGON_FIRST_KILL: 12_000,
  DRAGON_RESPAWN_KILL: 500,
} as const;

// ── Mob spawner block entity (STR dungeon; RED-prep) ─────────────────────────
export const SPAWNER = {
  /** A player must be within this range for the spawner to run. */
  REQUIRED_PLAYER_RANGE: 16,
  /** Stops spawning once this many of its mob are already nearby (the ≤6 cap). */
  MAX_NEARBY: 6,
  /** Spawn attempts per firing. */
  SPAWN_COUNT: 4,
  /** Horizontal scatter (blocks) around the spawner. */
  SPAWN_RANGE: 4,
  /** Firing delay is a random value in [MIN_DELAY, MAX_DELAY] game ticks. */
  MIN_DELAY: 200,
  MAX_DELAY: 800,
} as const;

// ── Structures (STR-1..4) ────────────────────────────────────────────────────
// Region-grid placement: `spacing` chunks per region, `separation` kept off the
// far edge (offset range = spacing−separation). Buried structures sit below the
// surface; razed ones level the terrain to a common ground plane.
export const STRUCTURE = {
  VILLAGE: { spacing: 28, separation: 8 },
  DESERT_PYRAMID: { spacing: 24, separation: 8 },
  PILLAGER_OUTPOST: { spacing: 32, separation: 8 },
  DUNGEON: { spacing: 8, separation: 2 },
  DESERT_WELL: { spacing: 16, separation: 6 },
  TRAIL_RUINS: { spacing: 24, separation: 8 },
  BURIED_TREASURE: { spacing: 20, separation: 8 },
  SHIPWRECK: { spacing: 20, separation: 6 },
  OCEAN_RUINS: { spacing: 18, separation: 6 },
  OCEAN_MONUMENT: { spacing: 32, separation: 10 },
  ANCIENT_CITY: { spacing: 40, separation: 12 },
  NETHER_FORTRESS: { spacing: 22, separation: 8 },
  BASTION_REMNANT: { spacing: 26, separation: 10 },
  /** 1 in N bastions is the hoglin STABLE rather than the keep — the same
   *  per-site family pick the ocean ruins make, on a shared 13×13 footprint. */
  BASTION_STABLES_ONE_IN: 2,
  END_CITY: { spacing: 20, separation: 8 },
  /** The end city's side annex, offset clear of the 9-wide tower footprint. */
  END_CITY_ANNEX_DX: 10,
  END_CITY_ANNEX_DZ: 1,
  /** …and the moored end ship: 1-in-N cities have one, floating clear of the
   *  island on the tower's far side (it is stamped with no ground layer, since a
   *  hull that buried itself into the rock would be a purpur shed, not a ship). */
  END_SHIP_ONE_IN: 3,
  /** …and the second TOWER with the bridge that reaches it: 1 in N cities grow
   *  one, north of the keep and clear of both the annex (east) and the ship
   *  (west), so the three wings never contend for the same ground. */
  END_CITY_TOWER_ONE_IN: 2,
  END_CITY_TOWER_DX: 1,
  END_CITY_TOWER_DZ: -20,
  END_CITY_BRIDGE_DX: 2,
  END_CITY_BRIDGE_DZ: -14,
  END_SHIP_DX: -7,
  END_SHIP_DY: 12,
  END_SHIP_DZ: 2,
  /** Fixed world Y the ancient city's floor sits at (the deep dark, well under
   *  the deepslate line and clear of the MIN_Y bedrock floor). */
  ANCIENT_CITY_Y: -48,
  /** 1 in N thrown eyes of ender shatters (Minecraft's own odds). */
  EYE_BREAK_ONE_IN: 5,
  /** The stronghold (DIM-3): rarer than anything else, and at a FIXED Y like the
   *  ancient city — deep in stone under hill and seabed alike, above the
   *  deepslate line so its walls read as the stone-brick ruin they are. */
  STRONGHOLD: { spacing: 64, separation: 24 },
  STRONGHOLD_Y: 12,
  /** Offsets of the complex around the portal room (11×11 at the origin): a
   *  corridor east to the library, another west to the storeroom. */
  STRONGHOLD_CORRIDOR_DX: 10,
  STRONGHOLD_CORRIDOR_DZ: 3,
  STRONGHOLD_LIBRARY_DX: 22,
  STRONGHOLD_LIBRARY_DZ: 1,
  STRONGHOLD_WEST_CORRIDOR_DX: -12,
  STRONGHOLD_STOREROOM_DX: -20,
  STRONGHOLD_STOREROOM_DZ: 1,
  /** Blocks a `medium:'water'` structure's seafloor must sit below sea level. */
  SUBMERGED_MIN_DEPTH: 4,
  /** Max surface-height range over a footprint before it's rejected as too steep. */
  FLATNESS_MAX_RANGE: 5,
  /** The nether floor is far rougher than overworld terrain, so its structures use
   *  a looser bound (they bury and carve their own halls regardless). */
  NETHER_FLATNESS_MAX_RANGE: 14,
  /** End islands are small and lumpy; a tower only needs a level-ish 9x9 pad. */
  END_FLATNESS_MAX_RANGE: 3,
  /** Blocks a razed structure clears above its ground plane (yard headroom). */
  RAZE_CLEAR: 6,
  /** Depth (blocks) below the surface a dungeon room's floor sits. */
  DUNGEON_DEPTH: 8,
} as const;

// ── Enchanting / anvil (PLR-5/6) ─────────────────────────────────────────────
export const ENCHANT = {
  /** Max effective bookshelves around an enchanting table. */
  SHELF_MAX: 15,
  /** Anvil "too expensive" cap, in levels. */
  ANVIL_CAP_LEVELS: 39,
} as const;

// ── Lighting & day/night (LIT-1..6; §5.2). Two 0..15 channels. daylightFactor
//    scales sky light per-frame in the shader (LIT-5), so dusk needs no remesh. ─
export const LIGHT = {
  /** Maximum light level per channel. */
  MAX: 15,
  /** Full daylight sky scale. */
  DAY: 1.0,
  /** Night sky-light scale — moonlight reads ≈ level-4 (LIT-6). */
  NIGHT_FLOOR: 0.22,
  /** Dawn/dusk ramp length, in ticks (smooth sunrise/sunset). */
  RAMP_TICKS: 600,
  /** Tick-of-day the sun begins to set (day→night ramp start). */
  DUSK_START: 12_000,
  /** Moon-phase night-floor modulation amplitude (±, LIT-6). */
  MOON_AMPLITUDE: 0.03,
  /** Distinct moon phases in the lunar cycle. */
  MOON_PHASES: 8,
  /** Rendered-brightness curve base: brightness = CURVE_BASE^(MAX−level). */
  CURVE_BASE: 0.86,
  /** Ambient floor so nothing renders pure black. */
  MIN_BRIGHTNESS: 0.05,
} as const;

// ── Ore vertical distribution (GEN-7; §13 table). Bands only — vein size /
//    tries-per-chunk live in data/features/ores.json (authored M2.3). y values
//    are world Y. `triangular` peaks at `peak`; `uniform` is flat min..max. ────
export type OreBand =
  | { readonly kind: 'uniform'; readonly min: number; readonly max: number }
  | { readonly kind: 'triangular'; readonly min: number; readonly peak: number; readonly max: number };

export const ORE_DIST: Readonly<Record<string, readonly OreBand[]>> = {
  coal: [{ kind: 'triangular', min: 0, peak: 96, max: 190 }],
  iron: [
    { kind: 'triangular', min: -24, peak: 16, max: 56 },
    { kind: 'uniform', min: 80, max: 384 }, // mountain band
  ],
  copper: [{ kind: 'triangular', min: -16, peak: 48, max: 112 }],
  gold: [{ kind: 'triangular', min: -64, peak: -16, max: 32 }],
  redstone: [
    { kind: 'uniform', min: -64, max: -32 },
    { kind: 'triangular', min: -32, peak: -58, max: 15 },
  ],
  lapis: [{ kind: 'triangular', min: -64, peak: 0, max: 64 }],
  // Java's diamond triangle nominally centers below the build floor; clamped to
  // WORLD.MIN_Y so the low tail still saturates near bedrock.
  diamond: [{ kind: 'triangular', min: -64, peak: -59, max: 16 }],
  emerald: [{ kind: 'triangular', min: -16, peak: 232, max: 320 }], // mountain biomes only
  nether_quartz: [{ kind: 'uniform', min: 10, max: 117 }],
  nether_gold: [{ kind: 'uniform', min: 10, max: 117 }],
  ancient_debris: [
    { kind: 'triangular', min: 8, peak: 15, max: 22 },
    { kind: 'uniform', min: 8, max: 119 }, // small secondary
  ],
} as const;

// ── Atlas mode (ATL-1/6; PLAN D14) ───────────────────────────────────────────
export const ATLAS = {
  /** First-run default — renders with zero network from the bundled fixture. */
  DEFAULT: {
    lat: 41.7366,
    lon: -72.6493,
    label: '269 Maxim Rd, Hartford, CT — Royal Charter Composite Squadron (KHFD)',
    /** Meters per block. */
    scale: 1,
  },
  /** Fetched/generated area, ~1 km on a side. */
  BBOX_M: 1000,
} as const;

// ── Real elevation (Part VI / M30, ELV-1..4; PLAN D21) ───────────────────────
export const ELV = {
  /** Datum block-Y: the lowest ground over the bbox rests here; relief rises above
   *  (ELV-2). Distinct from `ATLAS.SEA_LEVEL`-flat worlds, which stay at 63 so a
   *  no-elevation Atlas world is byte-identical to v1.1 (parity invariant). */
  BASE_Y: 62,
  /** Sentinel for a missing/void DEM sample; filled from nearest valid (E-26). */
  NO_DATA: -32768,
  /** One polite DEM tile request; this timeout → flat fallback (ELV-1). */
  FETCH_TIMEOUT_MS: 10_000,
  /** DEM grid resolution over the bbox (samples per side ≈ 30 m at BBOX_M=1000). */
  TILE_SAMPLES: 32,
} as const;

// ── Real solar position (Part VI / M31, SOL-1; PLAN D21) ─────────────────────
//    Atlas worlds drive daylight from the sun's true elevation (LIT-6 override).
export const SOLAR = {
  /** Sun elevation (degrees) at/above which it is full daylight. */
  DAY_ELEV_DEG: 6,
  /** Sun elevation (degrees) at/below which sky-light is the moonlit night floor
   *  — the civil-twilight ramp runs between the two. */
  NIGHT_ELEV_DEG: -6,
} as const;

// ── Aviation (Part VI / M32, AVI-1..3; PLAN D22) ─────────────────────────────
//    Atlas-only, opt-in flyable aircraft. The flight model is deliberately simple
//    and readable (PLAN §4 — not FAA-grade); the AVI_* numbers below were tuned
//    ONCE to fly a stable KHFD pattern and are contract values (§1 no magic
//    numbers, see docs/decisions.md M32). Distances are blocks (≈metres), time is
//    ticks (TPS=20), angles radians unless a *_DEG name says otherwise.
export const AVI = {
  /** Aircraft mass (arbitrary units; only ratios vs lift/thrust matter). */
  MASS: 1.0,
  /** Max thrust (accel units/tick) along the nose at full throttle. */
  THRUST_MAX: 0.050,
  /** Parasitic-drag coefficient: drag accel = DRAG_K·|v|² opposing velocity. */
  DRAG_K: 0.0016,
  /** Lift coefficient scale: lift accel = LIFT_K·|v|²·CL(AoA) perpendicular to v. */
  LIFT_K: 0.010,
  /** Lift-curve slope: CL = CL_SLOPE·AoA (per radian) until the stall break. */
  CL_SLOPE: 5.7,
  /** Stall angle of attack (radians ≈ 15°); past it lift collapses. */
  AOA_STALL: 0.26,
  /** Post-stall residual lift fraction (a stalled wing still bites a little). */
  CL_STALL_RESIDUAL: 0.35,
  /** Gravity accel (units/tick) pulling the aircraft down — matches player GRAVITY. */
  GRAVITY: 0.08,
  /** Height (blocks) above solid ground at/under which the gear is down (ground mode). */
  GEAR_H: 1.0,
  /** Rolling friction multiplier applied to horizontal speed while on the ground
   *  (low — a runway rolls freely so the plane accelerates past takeoff speed). */
  GROUND_FRICTION: 0.995,
  /** Impact speed (blocks/tick) above which a collision is a crash, not a landing (E-28). */
  CRASH_SPEED: 0.55,
  /** Crash damage dealt to a riding player on a hard impact (E-28). */
  CRASH_DAMAGE: 12,
  /** Control authority (radians/tick) at full deflection for pitch / roll / yaw. */
  PITCH_RATE: 0.020,
  ROLL_RATE: 0.035,
  YAW_RATE: 0.012,
  /** Max bank angle (radians ≈ 40°): holding aileron settles here, not a spiral. */
  ROLL_MAX: 0.70,
  /** Max pitch attitude (radians ≈ 26°): since pitch is a trimmed attitude, holding
   *  the stick must settle at a strong climb/descent, NOT run away to vertical where
   *  the wing stalls and the plane hangs and sinks (a good climb holds up to ~0.55). */
  PITCH_LIMIT: 0.45,
  /** Control-surface centering: deflection eases back toward neutral each tick. */
  CONTROL_DAMP: 0.85,
  /** Weathervane: the nose eases toward the flight-path heading each tick, so a
   *  bank curves the path AND the aircraft turns with it (a coordinated turn). */
  WEATHERVANE: 0.06,
  /** Throttle change per tick when the player holds throttle up/down. */
  THROTTLE_RATE: 0.03,
  /** Aircraft collision AABB half-extents (x/z) and half-height (y), blocks. */
  HALF: 0.8,
  HALF_H: 0.5,
  /** Feet per block, for the altimeter's Atlas/Earth reading (§29.10) — Atlas/Earth's
   *  ENU frame is 1 block = 1 metre (ATL-3/ATLAS_GROUND_Y), so this is the plain
   *  metres→feet constant. */
  FEET_PER_BLOCK: 3.28084,
  /** Blocks/tick → m/s, for the airspeed indicator (§29.10): velocity is stored per
   *  tick everywhere in the sim, and 1 block = 1 metre, so ×TPS is the whole
   *  conversion — named for what it's FOR rather than reusing `WORLD.TPS` under a
   *  second name (`sim/config.ts` constants don't import each other). */
  TPS_FOR_SPEED: 20,
  /** m/s → knots, for the airspeed indicator (§29.10). */
  KNOTS_PER_MPS: 1.94384,
  /** "tune-the-autopilot" (M54.1, §29.12): altitude error (blocks) at/under which a
   *  tick counts toward the hold streak, and consecutive ticks needed to complete
   *  the objective. */
  AUTOPILOT_HOLD_ERROR: 5,
  AUTOPILOT_HOLD_TICKS: 600,
} as const;

// ── Aeroway raster (Part VI / M32, AVI-1) ────────────────────────────────────
//    OSM aeroway=runway|taxiway|apron|helipad → flat concrete strips (M32). Widths
//    in METRES (divided by the Atlas scale → blocks, like ROAD_WIDTH_M).
export const RUNWAY = {
  /** Default runway width (m) when the OSM `width` tag is absent (ARCH §13). */
  WIDTH_DEFAULT_M: 45,
  /** Default taxiway / apron edge width (m). */
  TAXIWAY_WIDTH_M: 15,
  /** Threshold-bar / touchdown paint depth from each runway end, in blocks. */
  THRESHOLD_BLOCKS: 6,
  /** Centerline dash + gap length (blocks) for the dashed white centerline. */
  CENTERLINE_DASH: 3,
  /** Synthesized sandbox airstrip (blocks) when an aviation world has no OSM
   *  aeroway — a centred KHFD-oriented (≈020°/200°) training runway. Documented
   *  in docs/decisions.md M32; real OSM runways always take precedence. */
  SYNTH_LENGTH: 640,
  SYNTH_WIDTH_M: 45,
  SYNTH_BEARING_DEG: 20,
} as const;

// ── Deterministic replay (Part VI / M33, REP-1..5; PLAN D23) ─────────────────
//    Input-only recordings that re-simulate the pure §2 sim bit-for-bit. A frame
//    stream + periodic world-hash keyframes is kilobyte-scale (inputs, not chunks);
//    the player rebuilds the world from {seed, options} and re-feeds the frames,
//    asserting the hash on every crossed keyframe (E-31 stop-on-drift).
export const REP = {
  /** Ticks between keyframes = a `hashWorld` anchor for seeking + drift detection
   *  (1200 ticks = 60 s @ 20 TPS). */
  KEYFRAME_TICKS: 1200,
  /** Length (seconds) of the always-on instant-replay input ring the player can
   *  pop straight into the viewer. */
  INSTANT_SECONDS: 30,
  /** Playback speeds the scrubber offers (REP-4). */
  SPEEDS: [0.25, 0.5, 1, 2, 4, 8] as const,
  /** `.bbreplay` struct version — bumped on any incompatible format change; a newer
   *  version is rejected with a clear message, never crashed (E-33). */
  FORMAT_VERSION: 1,
} as const;

// ── Script blocks (SCR-1..4 / E-30, M35) — the safe, deterministic command-block
//    successor. Every limit is a HARD ceiling: overflowing one aborts *that block
//    for that tick* with an in-world error, never throwing into the tick loop. ──
export const SCR = {
  /** Max VM steps (statements + evaluated expression nodes) a single script block
   *  may run in one tick before it aborts that tick (E-30). Keeps a pathological
   *  or accidental script from stalling the sim. */
  OP_BUDGET: 10_000,
  /** Max nesting depth of `{}` blocks / `if` / `repeat` before abort (E-30), and
   *  since §27.2 the same budget a user `fn` call frame spends — one limit, not two.
   *  The call graph is proven acyclic at compile time, so this bounds a finite
   *  chain rather than guarding against recursion. */
  MAX_DEPTH: 32,
  /** Largest volume a single `setblock`/`fill` verb may touch (SCR-2). A `fill`
   *  exceeding this aborts the tick rather than editing a mega-region. */
  FILL_MAX: 4096, // 16³
  /** Largest count a single `repeat n {…}` may request; a larger n clamps to this
   *  (the loop stays bounded — there is no `while`). Total work is still capped by
   *  OP_BUDGET. */
  REPEAT_MAX: 4096,
  /** Longest `list` value the language allows (SCR-5, §27.2/§27.20). `list(…)` with
   *  more elements, or a `push` onto a full list, aborts that dispatch (E-30) — a
   *  list is an immutable value, so this also bounds the per-`push` O(n) copy. */
  LIST_MAX: 256,
  /** Search radius (blocks) for `nearestEntity`. */
  ENTITY_RANGE: 32,
  /** Script blocks allowed per chunk (SCR-3); placing beyond the cap is refused. */
  BLOCKS_PER_CHUNK: 16,
  /** `script_block` source struct version — a newer version is rejected gracefully
   *  on load/import, never crashed (E-33, shared convention with REP/FRG).
   *  **1 → 2 at M44** (§27.2): language v2 added `fn`, lists, seeded randomness and the
   *  capability block. A v1 source loads as v2 unchanged (the additions are a strict
   *  superset), which is why the bump needs no migration. */
  SOURCE_VERSION: 2,
  /** Longest error ring kept per script for the editor's diagnostics (§27.16). */
  ERROR_RING: 16,
  /** Per-entity script memory (§27.7, SCR-8). Memory is saved with the entity and
   *  NEVER replicated, so these caps bound save size, not bandwidth. */
  MEM: {
    /** Distinct keys one entity may hold; the 17th write aborts the dispatch. */
    KEYS: 16,
    /** Longest string value; a 65-character write aborts the dispatch. */
    STR_MAX: 64,
  },
  /** `say` rate limits (§27.8). Over-limit lines are DROPPED in entity-id order —
   *  never an abort, because a chatty script is not a broken one. */
  SAY: {
    /** Minimum ticks between two lines from the same entity. */
    ENTITY_TICKS: 20,
    /** Lines per second across the whole world. */
    WORLD_PER_SEC: 5,
  },
  /** The mob slice's own envelope (§27.8). Blocks keep the SCR.* budget above; a mob
   *  is cheaper per dispatch but there are far more of them, so it trades ops for
   *  population and pays a 5 Hz cadence. */
  MOB: {
    /** Ops per dispatch — deliberately ~20× tighter than a block's. */
    OP_BUDGET: 512,
    /** Dispatch every Nth tick, entities grouped by `entityId % N` ⇒ 5 Hz at 20 TPS.
     *  A `scriptHz: 20` opt-in sets the divisor to 1 and quarters the budget, so the
     *  per-second cost is unchanged (§27.20). */
    TICK_DIVISOR: 4,
    /** Total script ops one frame may spend across all entities; the tail defers to
     *  the next tick in entity-id order, so deferral is deterministic. */
    FRAME_CEILING: 50_000,
    /** Cells one dispatch may `setblock` — a mob edits its surroundings, not terrain. */
    SETBLOCK_MAX: 4,
    /** Consecutive aborts before the TYPE is quarantined to its archetype (§27.18).
     *  Quarantine is cleared by an edit or a save→load, never silently. */
    QUARANTINE_K: 3,
  },
  /** §27.10's four cascade bounds — what stops a block that reacts to a block that
   *  reacts from running forever. Distinct from `SCR.MAX_DEPTH` above, which bounds
   *  `{}` NESTING inside one dispatch; these bound dispatches ACROSS each other. */
  CASCADE: {
    /** A dispatch carries a depth; an `emit` or a script-caused edit at depth N
     *  delivers at N+1, and past this it is dropped and COUNTED. Deliberately small:
     *  four links is a reaction chain, more is a loop wearing one as a disguise. */
    MAX_DEPTH: 4,
    /** Lifecycle + signal dispatches that may RUN in one tick, across all scripts and
     *  all sources. It is a per-tick cap, not a queue bound: the overflow is dropped
     *  and counted, never carried into the next tick, because carrying it would let a
     *  cascade outlive the tick that caused it and starve the one after. */
    MAX_DISPATCH: 256,
    /** Script-caused spawns per tick (§27.10), on top of — never instead of — the
     *  MOB-4 category caps. */
    SPAWN_BUDGET: 8,
  },
  /** §27.10's `emit`/`on signal` reach. */
  SIGNAL: {
    /** Blocks an `emit` carries, modelled on RED-6 vibrations. */
    RADIUS: 32,
  },
  /** §27.12's trust tiers (SCR-17). The machinery ships; the gate stays shut — only ops
   *  author or install in the shipped default, so D-TF-81's threat model is unchanged by
   *  the tiers existing (D-TF-98). */
  TIER: {
    /** Whether a `foreign`-tier script (imported by a non-op, or from a collection whose
     *  hash is not allowlisted) may install at all. Off: it is refused at install with
     *  its COMPUTED capability set in the message (E-48). */
    FOREIGN_ENABLED: false,
    /** A foreign script's per-dispatch op budget — a quarter of a mob's normal one
     *  (D-TF-107): a reduced verb slice removes REACH but leaves CPU, and the tier exists
     *  precisely for the authors trusted least. Quartered again at `scriptHz: 20`, as
     *  every mob allowance is (§27.8). */
    FOREIGN_OP_BUDGET: 128,
  },
  /** §27.14's live content push (SCR-19, protocol v8) — bounds on ONE push, checked by the
   *  server before it parses anything, so an op's typo cannot become a memory spike. */
  PUSH: {
    /** Largest `ContentPush.patch` text (bytes of JSON) a server will look at. */
    MAX_BYTES: 262_144,
    /** Most script sources one push may carry. */
    MAX_SCRIPTS: 64,
  },
} as const;

// ── SAR / ground-team trainer (SAR-1..5 / E-32/34/35, M36) — the CAP mission ──
//    An ELT/ELF beacon a search crew homes on by received-signal-strength; the
//    signal is omnidirectional to a hard range, attenuates with distance (a
//    log-distance path-loss model), and is shadowed behind terrain. Two DF
//    instruments (Becker airborne dial + 9-LED handheld) show only relative
//    bearing — the trainee triangulates by moving/turning. Contract numbers (§1).
export const SAR = {
  /** Received-signal-strength (arbitrary dB-like scale) at 1 block from the ELT. */
  RSSI_P0: 0,
  /** Log-distance path-loss exponent: RSSI = P0 − 10·N·log10(dist) − terrainShadow. */
  PATHLOSS_N: 2.5,
  /** No-signal threshold: below this (after terrain shadow) the DF blanks (SAR-4). */
  RSSI_FLOOR: -80,
  /** Omnidirectional ELT range (blocks); no signal at all beyond it (SAR-3). */
  ELT_RANGE: 500,
  /** dB knocked off RSSI per block the terrain intrudes above the line of sight
   *  (a coarse LOS shadow against the elevation heightmap, SAR-2). */
  SHADOW_PER_BLOCK: 3,
  /** Ceiling on the terrain-shadow penalty (a hill can't make RSSI go to −∞). */
  SHADOW_MAX: 45,
  /** A "find" = staying within this radius (blocks) of the beacon (SAR-3). */
  FIND_RADIUS: 8,
  /** …for this many game ticks continuously (60 gt = 3 s @ 20 TPS). */
  DWELL: 60,
  /** Direction-finder LED count (odd → a true centre LED = dead-ahead, SAR-4). */
  DF_LEDS: 9,
  /** DF half field-of-view (degrees): beacon beyond ±this saturates the end LED. */
  DF_FOV_DEG: 90,
} as const;

// ── Classroom / guided-build (CLS-1..3, M37) — instructor ghost blueprints + a
//    placed-vs-plan progress diff. Pure overlay + diff over schematics (STR-2),
//    multiplayer (D19) and the edit log (PLR-1); no new sim mechanics (§1). ──
export const CLS = {
  /** How often (ticks) the host runs the cheap placed-vs-plan diff (20 gt = 1 s). */
  DIFF_INTERVAL_TICKS: 20,
  /** Largest blueprint (cells) the classroom compiles/overlays; a bigger schematic
   *  is refused rather than stamped — the diff + translucent overlay stay bounded. */
  MAX_CELLS: 8192,
  /** `blueprint` course struct version — a newer version is rejected gracefully on
   *  load/import, never crashed (E-33, shared REP/FRG/SCR convention). */
  SOURCE_VERSION: 1,
} as const;

// ── Tall-world proving ground + perf budgets (Part VII / TAL-6, M38.4). The
//    `?tall=1` dev flag boots a flat world at the DEFAULT EARTH column so the
//    engine's height-independent hot paths are exercised at extreme Y before the
//    real Earth generator lands (M40). TALL_PERF is tuned once here and the
//    measured run is logged in decisions.md (AVI-const pattern). ───────────────
export const TALL = {
  /** The proving-ground flat range = the default Earth column (ERT-5 / §23):
   *  MIN_Y −640, 10 752 blocks, 672 sections (Everest 8 849 m ≈ y 9 678 fits). */
  RANGE_MIN_Y: -640,
  RANGE_HEIGHT: 10752,
} as const;

export const TALL_PERF = {
  /** Worker gen budget per pristine tall chunk. */
  GEN_MS_PER_CHUNK: 8,
  /** Mesh budget per meshed section. */
  MESH_MS_PER_SECTION: 4,
  /** Single incremental relight (LIT-4, unchanged at range). */
  RELIGHT_MS: 2,
  /** Resident RAM per loaded pristine tall chunk (sparse sections, D27). */
  RAM_KB_PER_PRISTINE_CHUNK: 192,
} as const;

// ── EarthTiles /v1 contract client (Part VII / TIL-1..5, M39). The game consumes
//    the sibling EarthTiles service ONLY through this versioned HTTP contract — it
//    imports no code (ARCH §24 / ETD3). Major lives in the URL path; a major
//    mismatch or schema-invalid payload is a loud typed error, never a silent
//    fallback (E-38). Zooms + accuracy claims mirror EarthTiles' config. ───────────
export const TILE = {
  /** Contract major we speak. `/v1/...`; a `/v1/meta.contract.major` ≠ this → E-38. */
  CONTRACT_MAJOR: 1,
  /** Per-tile fetch deadline (ms). */
  TIMEOUT_MS: 10000,
  /** Slippy zoom per DEM source (z chosen so tile spacing ≈ source resolution). */
  DEM_Z: { '3dep-1m': 17, 'srtm-30m': 12 } as Record<string, number>,
  VECTOR_Z: 14,
  IMAGERY_Z: 17,
  /** Honest per-source vertical accuracy (m). The world claims ±1 block iff the DEM
   *  source's accuracy ≤ the world's metres-per-block scale (ERT-3). */
  ACCURACY_CLAIM_M: { '3dep-1m': 1, 'srtm-30m': 16 } as Record<string, number>,
} as const;

// ── Earth world type (Part VII / M40, ERT-1..6). A real place voxelized from TIL
//    tiles: sea-level datum (y = round(elevM/scaleM), y=0 = 0 m orthometric), a
//    per-world Y-range derived from the scale (ERT-5), and an offline-first default
//    (§13). The 3 ft/block default is an import-time mapping only — physics constants
//    never change (a 1.8-block player reads ~5′5″; accepted, not retuned). ─────────
export const EARTH = {
  /** First-run default — the same KHFD point as ATLAS, boots from the bundled
   *  `earthtiles-khfd` fixture with zero network (ERT-6). */
  DEFAULT: {
    lat: 41.7366,
    lon: -72.6493,
    label: ATLAS.DEFAULT.label,
    /** Metres per block; 0.9144 = 3 ft. Per-world editable at create (ERT-1). */
    scaleM: 0.9144,
  },
  /** Elevation span (metres orthometric) the world's block range covers (ERT-5).
   *  Dead-Sea shore −430 m and Everest 8 849 m both fit with dig/sky headroom. */
  RANGE_M: { MIN: -585, MAX: 9250 },
  /** Section clamp for very fine scales — elevations plateau past it (E-41). */
  MAX_SECTIONS: 2048,
  /** Datum block-Y: 0 m orthometric (sea level) rests here (ERT-1). */
  SEA_DATUM_Y: 0,
  /** Default bounded extent (m from centre); beyond → flat sea-level ocean (ERT-6). */
  EXTENT_M_DEFAULT: 2000,
  /** Depth (blocks) of the beyond-extent ocean shell below sea level: bedrock →
   *  seabed → water. Shallow so the ocean costs ~2 sections, not a full tall fill. */
  OCEAN_FLOOR_DEPTH: 32,
  /** Stone crust depth below the surface (blocks): bedrock rests here and the fill
   *  stops, so an Earth chunk costs like a normal one instead of filling to the
   *  −640 range floor. Matches vanilla's ~128-block surface→bedrock dig depth; the
   *  unreachable void below is the documented divergence. */
  CRUST_DEPTH: 128,
} as const;

// ── Reference overlays (Part VII / M40.4, OVL-1..3, E-40). A user image floats as a
//    horizontal ghost plane you align to the world — client render chrome ONLY, never
//    sim state (OVL-2/3). Earth-only in v1. Blobs live in the `overlays` IDB store;
//    small placements live in `WorldMeta.overlays[]`. ──────────────────────────────
export const OVL = {
  /** Max uploaded image size (bytes) — 25 MiB (E-40 rejects larger). */
  MAX_BYTES: 26_214_400,
  /** Max overlays per world (E-40). */
  MAX_PER_WORLD: 8,
  /** XYZ nudge per keypress (blocks). */
  MOVE_STEP: 0.5,
  /** Default opacity a fresh overlay is placed at. */
  DEFAULT_OPACITY: 0.5,
  /** Default ground resolution (metres/pixel) → `widthBlocks = pxWidth × this / scale`. */
  DEFAULT_M_PER_PX: 0.25,
} as const;

// ── World doors (Part VII / M41, DOR-1..5). A craftable `world_door` block entity
//    binds to a target world and traverses by a world-session swap (inventory carries,
//    per-world positions persist); the parent never loads or renders the child (TARDIS).
//    Child worlds are owner-created from the door, bounded, and resizable under
//    refuse-shrink-past-builds. All graph/traversal logic is client-side (ENG-1). ─────
export const DOOR = {
  /** After a traversal the arriving player is immune to re-triggering a door for this
   *  many ticks (E-36 — cycles are legal, one session swap at a time). */
  TRAVERSAL_COOLDOWN_GT: 40,
  /** Default child-world square footprint (blocks per side); resizable (DOR-4). */
  CHILD_DEFAULT_SIZE: 256,
  /** Clamp for a child footprint (blocks per side). */
  MIN_SIZE: 16,
  MAX_SIZE: 8192,
} as const;

// ── Storage metering (Part VII / M41 local read-out, M42 server quota; STO-1..4).
//    Counts bytes of persisted player-caused data per world (pristine chunks are free,
//    ENG-10 regenerates them). The billing story is one seam (STO-4) — no payment code. ─
export const STO = {
  /** Per-account / local quota (bytes) — 256 MiB (STO-2). */
  DEFAULT_QUOTA_BYTES: 268_435_456,
  /** Usage read-out warns at this fraction of quota (STO-3). */
  WARN_FRACTION: 0.9,
} as const;

// ── SAR helicopter (SAR-5, M36) — a deterministic VTOL/hover model beside the
//    fixed-wing aircraft: collective (vertical thrust vs gravity), cyclic
//    (pitch/roll → horizontal accel), pedals (yaw); no stall. HELI_* tuned once
//    at M36 to hover and fly a stable pattern, logged in docs/decisions.md. ──
export const HELI = {
  /** Seats = pilot + 3 (SAR-5). */
  SEATS: 4,
  /** Max collective vertical thrust accel (blocks/tick²); > GRAVITY so it climbs. */
  COLLECTIVE_MAX: 0.12,
  /** Collective needed to hold a hover (= GRAVITY); the mid-stick trim point. */
  HOVER_COLLECTIVE: 0.08,
  /** Collective change per tick when the pilot holds up/down. */
  COLLECTIVE_RATE: 0.006,
  /** Gravity (matches the player/aircraft). */
  GRAVITY: 0.08,
  /** Cyclic tilt authority (radians/tick) at full deflection → body-frame accel. */
  CYCLIC_RATE: 0.03,
  /** Max cyclic tilt (radians ≈ 23°): the body leans this far to accelerate. */
  CYCLIC_MAX: 0.40,
  /** Horizontal accel per radian of cyclic tilt (blocks/tick² at full tilt). */
  CYCLIC_ACCEL: 0.06,
  /** Cyclic self-centres toward level when the stick is released. */
  CYCLIC_DAMP: 0.88,
  /** Pedal yaw rate (radians/tick) at full deflection. */
  PEDAL_RATE: 0.03,
  /** Horizontal + vertical velocity drag (a hovering body sheds speed). */
  DRAG_H: 0.90,
  DRAG_V: 0.94,
  /** Below this height over solid ground the skids are down (ground mode). */
  GEAR_H: 0.6,
  /** Vertical impact speed (blocks/tick) above which a touchdown is a crash (E-28). */
  CRASH_SPEED: 0.45,
  /** Crash damage to a riding pilot (E-28). */
  CRASH_DAMAGE: 12,
  /** Collision AABB half-extents (x/z) and half-height (y). */
  HALF: 1.0,
  HALF_H: 0.7,
} as const;

// ── SAR van (SAR-5, M36) — an 8-seat ground vehicle: throttle/brake/steer with
//    ground friction; terrain-follows up to VAN_STEP per move (Atlas), and in
//    Earth mode is additionally kept on drivable columns (E-35, M40). ──
export const VAN = {
  /** Seats (SAR-5). */
  SEATS: 8,
  /** Max step up/down per move (blocks); a ≥2-block wall blocks it (Atlas). */
  STEP: 1,
  /** Forward accel per tick at full throttle (blocks/tick²). */
  ACCEL: 0.03,
  /** Brake decel per tick (blocks/tick²). */
  BRAKE: 0.06,
  /** Top forward speed (blocks/tick). */
  MAX_SPEED: 0.5,
  /** Rolling friction each tick (coast-down). */
  FRICTION: 0.92,
  /** Steer rate (radians/tick) at full lock, scaled by how fast the van is rolling. */
  STEER_RATE: 0.05,
  /** Collision AABB half-extents (x/z) and half-height (y). */
  HALF: 1.0,
  HALF_H: 1.0,
} as const;

// ── Mini-map (UI-8; D15), view radii in blocks ───────────────────────────────
export const MINIMAP_RADII = [32, 64, 128] as const;

// ── Cartography (UI-8 / D15, M18) — crafted filled maps ──────────────────────
export const CARTO = {
  /** Map face resolution (pixels per side). */
  MAP_PX: 128,
  /** Scale a fresh map is created at (0 ⇒ 1 block/pixel, 128 blocks across). */
  DEFAULT_SCALE: 0,
  /** Max zoom-out level (each step doubles blocks/pixel). */
  MAX_SCALE: 4,
  /** Brightness multipliers for relief shade tiers 0(dark)/1(normal)/2(bright). */
  SHADE_MUL: [180 / 255, 220 / 255, 255 / 255] as const,
  /** North-slope dither thresholds picking the shade tier. */
  RELIEF_HI: 0.6,
  RELIEF_LO: -0.6,
  /** Blocks around the player a held map re-samples each update. */
  UPDATE_RADIUS: 48,
} as const;

// ── Amethyst geodes (GEN / M19.1) — budding-crystal growth ───────────────────
export const AMETHYST = {
  /** Odds a budding_amethyst sprouts/advances a bud per random tick (MC 1-in-5). */
  GROW_CHANCE: 0.2,
  /** Shards a broken amethyst_cluster drops (mirrored by its loot table). */
  CLUSTER_SHARDS: 4,
} as const;

// ── Sculk (Deep Dark / M22.1) — a catalyst absorbs a nearby death's XP and
//    spreads sculk over the exposed surfaces around it (the "charge" model). ──
export const SCULK = {
  /** A death within this many blocks (Chebyshev) of a sculk_catalyst blooms it. */
  CATALYST_RANGE: 8,
  /** How far sculk may spread (Chebyshev) from the bloom origin. */
  SPREAD_RANGE: 8,
  /** How far below the death to look for the floor a bloom seeds from. */
  FLOOR_DROP: 4,
} as const;

// ── Sculk sensor (M22.1 deep dark) — a vibration within range wakes the sensor,
//    which emits redstone power while active; a calibrated one filters by the
//    frequency set on its side input. ──
export const SCULK_SENSOR = {
  /** A vibration within this range (blocks) wakes a sculk sensor. */
  VIBRATION_RANGE: 8,
  /** Ticks the sensor stays active (powered) after a vibration. */
  ACTIVE_TICKS: 40,
  /** Redstone power an active sensor emits. */
  POWER: 15,
} as const;

// ── Beacon (M20.1) — a pyramid of mineral blocks under a beacon powers a status
//    effect over an area, scaling with the pyramid's tier. ──
export const BEACON = {
  /** Deepest (widest) complete pyramid layer a beacon reads. */
  MAX_TIER: 4,
  /** Ticks between effect reapplications (MC re-applies every 80 gt). */
  APPLY_INTERVAL: 80,
  /** Horizontal reach = tier·RANGE_PER_TIER + RANGE_BASE blocks. */
  RANGE_BASE: 10,
  RANGE_PER_TIER: 10,
  /** Effect duration = (DURATION_BASE + tier·DURATION_PER_TIER) seconds. */
  DURATION_BASE_SECONDS: 9,
  DURATION_PER_TIER_SECONDS: 2,
  /** Vertical extent of the effect box, above / below the beacon. */
  VERTICAL_UP: 320,
  VERTICAL_DOWN: 4,
} as const;

// ── Warden + sculk shrieker (M22.1) — the shrieker's warning ladder summons a
//    warden; the blind warden hunts whoever has angered it most and fires an
//    armour-piercing sonic boom. ──
export const WARDEN = {
  /** Shrieker warning levels 0..MAX; a shriek at SUMMON_LEVEL spawns a warden. */
  WARNING_MAX: 3,
  SUMMON_LEVEL: 3,
  /** Darkness effect duration (ticks) applied on a shriek. */
  DARKNESS_TICKS: 260,
  /** Ticks the warden spends emerging (immobile) after it spawns. */
  EMERGE_TICKS: 135,
  /** Anger caps here; decays ANGER_DECAY per second; hunts at/above ANGRY_THRESHOLD. */
  ANGER_MAX: 150,
  ANGER_DECAY: 1,
  ANGRY_THRESHOLD: 40,
  /** Anger a disturbance (sound/projectile) vs. a melee hit adds. */
  ANGER_DISTURBANCE: 10,
  ANGER_HIT: 35,
  /** Range + damage of the sonic boom; it bypasses armour and shields. */
  SONIC_RANGE: 20,
  SONIC_DAMAGE: 10,
  SONIC_BYPASS: true,
  /** Ticks between sonic booms, and the min range at which it prefers the boom. */
  SONIC_COOLDOWN: 60,
  SONIC_MIN_RANGE: 5,
  /** How far a warden hears a sculk vibration. */
  VIBRATION_RANGE: 16,
  /** Anger a heard vibration adds: a flat floor plus a share of its game-event
   *  frequency, so breaking a block angers far more than a footstep. */
  ANGER_PER_VIBRATION: 4,
  ANGER_PER_FREQUENCY: 1.5,
  /** Ticks between anger decays (the ledger decays ANGER_DECAY per interval). */
  DECAY_INTERVAL: 20,
  /** How far a hunting warden strikes a MOB quarry, and its melee cooldown. */
  MOB_REACH: 2.2,
  MOB_STRIKE_COOLDOWN: 20,
  /** Distance a mob must move in a tick to count as a footstep vibration. */
  STEP_EPSILON: 0.05,
  /** Ticks of total calm before a pacified warden burrows away. */
  PACIFIED_DESPAWN_TICKS: 1200,
} as const;

// ── Horse attributes + jump charge (M19-adjacent) ────────────────────────────
// Each horse rolls its own speed/jump/health multipliers at spawn; a ridden one
// winds up a charged jump rather than a flat hop.
export const HORSE = {
  SPEED_MIN: 0.7, SPEED_MAX: 1.4,
  JUMP_MIN: 0.7, JUMP_MAX: 1.5,
  HEALTH_MIN: 0.7, HEALTH_MAX: 1.4,
  /** Ticks of holding jump to reach a full charge. */
  CHARGE_TICKS: 20,
  /** Below this charge the horse simply refuses (no limp hop). */
  MIN_CHARGE: 0.2,
  /** Upward impulse at a full charge and a 1.0 jump attribute. */
  BASE_JUMP: 0.52,
} as const;

// ── Heredity (HER-1..12, ARCH §28.15; Part IX) ───────────────────────────────
//    Individual animals and seeds. Tuned once at M50.7 and logged per the AVI-const
//    pattern. With the `heredity` gamerule off, NOTHING below is read (§28, D21).
export const HER = {
  /** Most genes one table may declare (§28.3). */
  GENES_MAX: 16,
  /** Per-gene mutation chance at each birth (§28.5). */
  MUTATION: 0.015,
  /** How far a polygenic mutation moves the byte (±, §28.5). */
  MUTATION_STEP: 32,
  /** Half-width of the triangular noise added to a polygenic child (§28.5). */
  NOISE: 24,
  /** Lifespan range in ticks — 96 000 = 80 in-game minutes (4 days) at 20 TPS (§28.8). */
  LIFE_MIN: 96_000,
  LIFE_MAX: 192_000,
  /** Carrying capacity: at most CAP_COUNT same-species adults within CAP_RADIUS
   *  blocks may be fed to breed (§28.8). */
  CAP_RADIUS: 16,
  CAP_COUNT: 24,
  /** Ticks between two "too crowded" action-bar messages to one player. */
  CAP_MSG_TICKS: 60,
  /** Temperament (§28.7). */
  COHESION_RADIUS: 6,
  COHESION_MAX: 0.35,
  CURIOUS_RADIUS: 6,
  STARTLE_RADIUS: 4,
  /** Records per notebook section (E-56). */
  NOTEBOOK_MAX: 256,
  /** Largest `heredityPace` (§28.2). */
  PACE_MAX: 4,
  /** Perf gate: mean ms per tick for the 100-animal scene (§28.14). */
  PERF_MS: 20,
  /** The five stalk-colour quintiles, green → gold (§28.10). */
  STALK_HEX: ['#3f8f2f', '#63a13a', '#8db54a', '#c2b84c', '#d9a441'],
} as const;

// ── Education & Content (EDU-1..10, ARCH §29.16; Part X) ────────────────────
//    Lesson packs, objectives, the quiz, results. Tuned once at M54.4 and logged
//    per the AVI-const pattern. With no active lesson and no subject collection
//    loaded, NOTHING below is read (§29, D21).
export const EDU = {
  /** Most objectives one lesson may declare (§29.2). */
  MAX_OBJECTIVES: 32,
  /** Most questions one quiz may declare (§29.2/§29.5). */
  MAX_QUESTIONS: 32,
  /** Most regions one lesson may declare (§29.2). */
  MAX_REGIONS: 16,
  /** Ticks between region-containment checks, per player (§29.4). */
  REGION_CHECK_TICKS: 4,
  /** Largest inline `.tfworld` template a lesson pack may carry (§29.2, D-TF-189). */
  TEMPLATE_MAX_BYTES: 2_097_152,
  /** Entries kept in a world's lesson log before the oldest is dropped (§29.8). */
  LOG_MAX: 2000,
  /** Largest hand-raise queue a roster tracks (§29.8). */
  HAND_QUEUE_MAX: 20,
  /** Most rows one export may emit (§29.9). */
  EXPORT_MAX_ROWS: 5000,
  /** Perf gate: mean ms per tick for the 20-student/32-objective scene (§29.15). */
  PERF_MS: 25,
  /** Ticks between each student's result row being re-sent to instructors, so the
   *  roster's ticks-on-task keeps rising (§29.8; a completion is sent at once). */
  ROSTER_PUSH_TICKS: 20,
} as const;

// ── Original mobs: meadow_vole + barn_owl (M53.2/.3, ARCH §29.11, CNT-2/3) ───
export const VOLE = {
  /** Lifespan gene range (ticks) — deliberately short so generations turn over in
   *  minutes at `heredityPace 4` (§29.11's own reason for the constant). */
  LIFE_MIN: 24_000,
  LIFE_MAX: 48_000,
} as const;

export const OWL = {
  /** How far a hunting owl searches for a `meadow_vole` (§29.11). */
  HUNT_RADIUS: 24,
  /** Only hunts when block light at its position is BELOW this (§29.11). */
  HUNT_LIGHT_MAX: 7,
  /** Ticks of rest after a kill before it may pick a new target (§29.11). */
  REST_TICKS: 1200,
} as const;

export const BEE = {
  /** Pollination range (blocks) a hive-adjacent bee's `pollenMate` may reach,
   *  beyond the four orthogonal neighbours (M50), with `heredity` on (§29.11). */
  POLLINATION_RANGE: 8,
} as const;

// ── Co-working (COW-1..12, ARCH §30; M55–M57) ────────────────────────────────
//    Sticky note / task board / timer / the shared screen. D21: without the
//    `coworking` collection none of these blocks exist and every golden is
//    byte-identical to pre-M55. Tuned once at M57.5.
export const COW = {
  /** Sticky-note text cap (chars) — over it, truncated at the edit boundary,
   *  never refused (E-63). */
  NOTE_MAX: 48,
  /** Task-board line count / per-line char cap. */
  BOARD_LINES: 8,
  LINE_MAX: 40,
  /** Largest custom timer duration (ticks) a `TimerEdit` may request. */
  TIMER_MAX: 72_000,
  /** Radius (blocks) that gets the `Timer finished.` action-bar line. */
  TIMER_RADIUS: 16,
  /** Preset durations (ticks): Pomodoro 25 min, break 5 min, at 20 TPS. */
  POMODORO: 30_000,
  BREAK: 6_000,
  /** Most simultaneous viewers one shared screen serves (COW-7). */
  MAX_VIEWERS: 4,
  /** The video quad's offset in front of its block face (blocks) — the exact
   *  OVL-2 ghost-plane construction (`client/earth/overlay.ts`). */
  SCREEN_OFFSET: 0.01,
  /** Largest screen-block quad the client joins into one video plane. */
  SCREEN_MAX_W: 3,
  SCREEN_MAX_H: 2,
  /** Capture settings (COW-8): VP8, text-hint, no audio (voice is a named
   *  non-goal, COW-12). */
  VIDEO: { w: 1280, h: 720, fps: 10, codec: 'VP8', hint: 'text', audio: false },
  /** coturn's UDP relay port range (NET-8, D-TF-196). */
  TURN_RELAY_RANGE: '49160-49200/udp',
  /** ICE negotiation timeout (ms) before a viewer is told E-64's line. */
  ICE_TIMEOUT_MS: 15_000,
} as const;

// ── Fish schooling (MOB-2, M18-adjacent) ─────────────────────────────────────
// Cod/salmon/tropical fish steer by a two-rule flock (cohesion + separation) over
// a wander heading, and bolt from a nearby player. Alignment is deliberately
// skipped — the wander heading already does that job at this scale.
export const FISH = {
  /** How far a fish looks for schoolmates, and how close is too close. */
  SCHOOL_RANGE: 8,
  SEPARATION_RANGE: 1.2,
  /** Steering weights: pull to the school centre vs. push off a neighbour. */
  COHESION: 0.6,
  SEPARATION: 1.0,
  /** Inside this distance of the school centre, cohesion switches off (no jitter). */
  COHESION_DEADZONE: 0.5,
  /** How strongly the drifting wander heading counts against the flock rules. */
  WANDER_WEIGHT: 0.5,
  /** A player this close makes a fish bolt (overrides schooling entirely). */
  FLEE_RANGE: 5,
  /** Ticks between re-picking a wander heading. */
  WANDER_INTERVAL: 40,
  /** Fish are slower than their raw `speed` suggests (it is tuned for walkers). */
  SPEED_SCALE: 0.5,
} as const;

// ── Farming (M11.1) — soil tuning shared by farmland and hydroponic trays. ──
export const FARMING = {
  /** Fully-hydrated farmland level; a hydroponic tray always sits here. */
  MOISTURE_MAX: 7,
  /** Extra produce a mature crop yields when grown on a hydroponic tray. */
  HYDRO_YIELD_BONUS: 2,
} as const;

// ── Swimming mobs (MOB-3): squid/fish/dolphin/guardian move in 3D inside fluid. ──
export const SWIM = {
  /** Per-tick easing toward the desired velocity (matches the flier's feel). */
  ACCEL: 0.2,
  /** Max swim speed (blocks/tick) — below FLYING.MAX_SPEED; water is thick. */
  MAX_SPEED: 0.11,
  /** How far past the body edge to test for water before moving that way; keeps
   *  a mob from clipping its nose out of the sea. */
  EDGE_PROBE: 0.1,
  /** Horizontal damping while beached, so a flopping fish doesn't slide away. */
  BEACHED_DRAG: 0.8,
} as const;

// ── Ocean fauna (M18.1) — a dolphin grants Dolphin's Grace to a nearby swimmer; a
//    pufferfish puffs up and poisons anyone who touches it. ──
export const OCEAN = {
  /** Range (blocks) a dolphin grants Dolphin's Grace. */
  DOLPHIN_GRACE_RANGE: 8,
  GRACE_TICKS: 100,
  /** Range a pufferfish inflates at, and the contact range it poisons within. */
  PUFF_RANGE: 4,
  PUFF_CONTACT: 1.5,
  POISON_TICKS: 140,
} as const;

// ── Respawn anchor (M20.1) — glowstone-charged; sets spawn in the Nether, but
//    detonates like a bed in any other dimension. ──
export const ANCHOR = {
  /** Maximum glowstone charges. */
  MAX_CHARGES: 4,
  /** Explosion power when used outside the Nether (bed-like). */
  EXPLOSION_POWER: 5,
} as const;

// ── Dripstone (M19.1) — pointed dripstone: a stalagmite amplifies fall damage, a
//    water-fed stalactite grows down and drips into a cauldron. ──
export const DRIPSTONE = {
  /** A falling stalactite needs this much drop before it hurts, then scales per
   *  block fallen up to a cap (M19.1 remainder). */
  IMPACT_MIN_FALL: 3,
  IMPACT_PER_BLOCK: 2,
  IMPACT_MAX: 40,
  /** Longest connected spike run that falls as one piece. */
  MAX_COLUMN: 8,
  /** Fall damage onto an up-pointing stalagmite is multiplied by this (MC ×2). */
  FALL_MULTIPLIER: 2,
  /** Contact damage a stalagmite deals to something walking into its tip. */
  CONTACT_DAMAGE: 2,
  /** Per-random-tick chance a water-fed stalactite grows one block down. */
  GROW_CHANCE: 0.011,
  /** A dripped-into cauldron fills up to this level. */
  CAULDRON_MAX_LEVEL: 3,
} as const;

// ── Trial chambers (M22.1) — a trial spawner scales its wave with the number of
//    nearby players; a vault rewards each player once for a trial key. ──
export const TRIAL = {
  /** Mobs a normal trial spawner releases: BASE + (players−1)·PER_PLAYER. */
  BASE_TOTAL: 6,
  PER_PLAYER: 2,
  /** Ominous variant releases more. */
  OMINOUS_BASE_TOTAL: 12,
  OMINOUS_PER_PLAYER: 5,
  /** How many may be alive at once: SIM_BASE + (players−1)·SIM_PER_PLAYER. */
  SIM_BASE: 2,
  SIM_PER_PLAYER: 1,
  /** Ticks between the spawner's own release attempts, and its post-wave cooldown. */
  SPAWN_INTERVAL: 40,
  COOLDOWN_TICKS: 600,
  /** How near a player must be to wake the spawner. */
  DETECT_RADIUS: 14,
} as const;

// ── Ender dragon (M21.1) — a flying boss healed by end crystals; killable only
//    once they are all destroyed, then it perches and drops the dragon egg. ──
export const DRAGON = {
  /** Phase cycle length; the first CIRCLE_TICKS of it are circling, the rest strafe. */
  CYCLE_TICKS: 300,
  CIRCLE_TICKS: 200,
  /** Below this health fraction (with no crystals) the dragon perches, vulnerable. */
  PERCH_FRACTION: 0.35,
  /** HP regenerated per heal tick, per intact end crystal. */
  HEAL_PER_CRYSTAL: 2,
  /** Ticks between crystal heals. */
  HEAL_INTERVAL: 10,
  /** How far (blocks) an end crystal must be from the dragon to heal it. */
  HEAL_RADIUS: 64,
  /** Explosion damage a destroyed end crystal deals. */
  CRYSTAL_BLAST: 6,
} as const;

// ── Conduit (M18.1) — a prismarine frame around a submerged conduit powers a
//    Conduit Power area (and, when fully framed, zaps a nearby hostile). ──
export const CONDUIT = {
  /** Frame blocks needed to activate (MC minimum). */
  MIN_FRAME: 16,
  /** Range = ⌊frame/7⌋·RANGE_STEP blocks. */
  RANGE_STEP: 16,
  /** Cap on the Conduit Power radius (a full 42-block frame). */
  MAX_RANGE: 96,
  /** Conduit Power effect duration (ticks), reapplied on the interval. */
  EFFECT_TICKS: 260,
  /** Ticks between reapplications / hostile zaps (MC: every 2 s). */
  APPLY_INTERVAL: 40,
  /** A frame this large starts zapping hostiles. */
  ATTACK_FRAME: 42,
  /** Radius the conduit zaps a hostile within, and the damage it deals. */
  ATTACK_RADIUS: 8,
  ATTACK_DAMAGE: 4,
} as const;

// ── Wither (M20.1) — a 3-phase flying boss summoned from a soul-block frame + 3
//    wither skeleton skulls; drops the nether star. ──
export const WITHER = {
  /** Charge-up ticks after spawn: hovers, invulnerable, then bursts to phase 1. */
  SUMMON_TICKS: 220,
  /** At/below this fraction of max health the wither enters the faster phase 2. */
  PHASE2_HEALTH_FRACTION: 0.5,
  /** Ticks between skull shots in each phase. */
  SKULL_INTERVAL_PHASE1: 40,
  SKULL_INTERVAL_PHASE2: 20,
} as const;

// ── Guardian (M18.1) — ocean-monument hostile: a charge-up laser beam, defensive
//    spikes that thorn a melee attacker, and (elder) a Mining Fatigue aura. ──
export const GUARDIAN = {
  /** Ticks a guardian charges its beam (line of sight must hold the whole time). */
  BEAM_CHARGE_TICKS: 80,
  /** Beam range (blocks) — beyond this it cannot lock on. */
  BEAM_RANGE: 15,
  /** Beam damage on discharge; the elder's beam hits harder. */
  BEAM_DAMAGE: 6,
  ELDER_BEAM_DAMAGE: 8,
  /** Thorn damage reflected onto a melee attacker while the spikes are extended. */
  SPIKE_DAMAGE: 2,
  ELDER_SPIKE_DAMAGE: 3,
  /** Range (blocks) the elder guardian's Mining Fatigue aura reaches. */
  FATIGUE_RANGE: 50,
  /** Mining Fatigue amplifier (0-based → III) and duration it applies (MC 5 min). */
  FATIGUE_AMPLIFIER: 2,
  FATIGUE_TICKS: 6000,
  /** Ticks between elder-aura reapplications. */
  FATIGUE_INTERVAL: 1200,
  /** A guardian out of water dries out, taking tick damage on this cadence. */
  DRY_INTERVAL: 20,
} as const;

// ── Nether fauna (M17.1) — hoglin (zombifies to a zoglin in the overworld, fears
//    warped fungus) + strider (fire-immune on lava, shivers/slows off it, hurt by
//    water, steered by a warped fungus on a stick). ──
export const NETHER_FAUNA = {
  /** Ticks a hoglin must spend outside the Nether before it zombifies to a zoglin. */
  HOGLIN_ZOMBIFY_TICKS: 300,
  /** Cold (off-lava) strider walk-speed multiplier. */
  STRIDER_COLD_SPEED: 0.35,
  /** Warped-fungus-on-a-stick steer speed multiplier while ridden. */
  STRIDER_STEER_BOOST: 1.6,
  /** A wet strider takes a point of damage on this cadence. */
  STRIDER_WATER_INTERVAL: 20,
} as const;

// ── Turtle (M18.1 → ties M17 turtle helmet) — eggs crack + hatch at night on
//    sand; a baby grown to an adult drops a scute; a worn turtle helmet grants
//    Water Breathing while (and briefly after) the head is submerged. ──
export const TURTLE = {
  /** Water Breathing granted/refreshed by a worn turtle helmet (10 s, persists). */
  HELMET_BREATH_TICKS: 200,
  /** Crack stages an egg passes through before it can hatch. */
  EGG_CRACKS: 2,
  /** Chance a mob/player stepping on an egg tramples (breaks) it. */
  TRAMPLE_CHANCE: 0.15,
  /** Scute needed to craft a turtle helmet (MC = 5). */
  SCUTE_PER_HELMET: 5,
} as const;

// ── Allay (M18.1-adjacent) — a friendly flyer that likes the item it's handed,
//    fetches matching drops, and duplicates when dancing by a note block if given
//    an amethyst shard. ──
export const ALLAY = {
  /** Ticks before a duplicated allay can duplicate again. */
  DUP_COOLDOWN_TICKS: 6000,
  /** Range an allay picks up a matching dropped item within. */
  PICKUP_RANGE: 16,
  /** How near a playing note block sets an allay dancing (duplication window). */
  NOTE_DANCE_RANGE: 3,
} as const;

// ── Breeze + wind charge (M22.1 trial chambers) — the breeze fires wind charges
//    that burst on impact, knocking entities back and toggling activatable blocks. ──
export const BREEZE = {
  /** Radius (blocks) of a wind-charge burst. */
  BURST_RADIUS: 3.5,
  /** Peak knockback impulse at the burst centre (falls off linearly to the edge). */
  KNOCKBACK: 1.2,
  /** Ticks between a breeze's wind-charge shots. */
  FIRE_INTERVAL: 60,
  /** Direct-hit damage of a wind charge (mostly knockback in MC). */
  DAMAGE: 1,
  /** Upward impulse when a player throws a wind charge at their own feet (self-launch). */
  SELF_LAUNCH: 0.7,
} as const;

// ── Archaeology (M22-adjacent) — brushing a suspicious sand/gravel block over
//    several stages reveals a buried find (a pottery sherd or a common drop) and
//    collapses the block into plain sand/gravel. ──
export const ARCHAEOLOGY = {
  /** Brush stages a suspicious block passes through before it reveals its find. */
  BRUSH_STAGES: 4,
} as const;

// ── Sniffer (M19-adjacent) — the ancient mob hatched from an archaeology egg; it
//    snuffles over diggable ground and unearths torchflower seeds. ──
export const SNIFFER = {
  /** Ticks between a sniffer's dig attempts. */
  DIG_INTERVAL: 120,
  /** Odds a dig attempt unearths a seed. */
  DIG_CHANCE: 0.5,
} as const;

// ── Camel (M19-adjacent) — a tall two-seat desert mount that dashes forward on a
//    cooldown and sits/stands. ──
export const CAMEL = {
  /** Horizontal impulse (blocks/tick) of a camel's dash. */
  DASH_IMPULSE: 0.9,
  /** Upward hop added to the dash. */
  DASH_HOP: 0.35,
  /** Ticks before a camel may dash again. */
  DASH_COOLDOWN: 55,
  /** Passenger seats (a camel carries two). */
  SEATS: 2,
} as const;

// ── Persistence ──────────────────────────────────────────────────────────────
export const AUTOSAVE_MS = 30_000;

// ── Performance gates (PLAN D17 / §3.4). Asserted by perf specs each milestone.
export const PERF = {
  TARGET_FPS: 60,
  RENDER_DIST_GATE: 8,
  /** Max main-thread ms/frame spent on mesh uploads. */
  UPLOAD_MS: 4,
  /** Single-torch incremental relight budget. */
  RELIGHT_MS: 2,
  /** Entity tick budget with 40 mobs. */
  ENTITY_TICK_MS: 4,
  /** Redstone contraption tick budget. */
  CONTRAPTION_TICK_MS: 3,
  HEAP_MB: 800,
  SERVER_TPS: 20,
  SERVER_PLAYERS: 8,
} as const;

/** Aggregate for convenient import; individual groups above are the canonical exports. */
export const CONFIG = {
  TICK,
  WORLD,
  VIEW,
  STREAM,
  REACH,
  PLAYER,
  FLUIDS,
  WEATHER,
  SLEEP,
  REDSTONE,
  RED,
  TNT,
  TNT_FUSE_GT,
  MINECART,
  PORTAL,
  NETHER,
  BURN,
  FIREBALL,
  FLYING,
  MOB,
  PROJECTILE,
  POTION,
  XP_ORB,
  BREED,
  CREEPER,
  RAID,
  TAME,
  GOLEM,
  SHEEP,
  ENDERMAN,
  SLIME,
  CHARGED_CREEPER,
  SURVIVAL,
  COMBAT,
  TOOLS,
  ITEMDROP,
  XP,
  ENCHANT,
  LIGHT,
  ORE_DIST,
  ATLAS,
  ELV,
  SOLAR,
  AVI,
  RUNWAY,
  REP,
  SCR,
  SAR,
  HELI,
  VAN,
  CLS,
  TALL,
  TALL_PERF,
  TILE,
  EARTH,
  OVL,
  DOOR,
  STO,
  MINIMAP_RADII,
  CARTO,
  AMETHYST,
  BEACON,
  CONDUIT,
  GUARDIAN,
  NETHER_FAUNA,
  TURTLE,
  ALLAY,
  SCULK_SENSOR,
  BREEZE,
  ARCHAEOLOGY,
  SNIFFER,
  CAMEL,
  WITHER,
  WARDEN,
  DRAGON,
  TRIAL,
  DRIPSTONE,
  ANCHOR,
  OCEAN,
  AUTOSAVE_MS,
  PERF,
} as const;
